/* eslint-disable consistent-return */
/**
 * Tasks V2 Migration — Super Admin cockpit slice.
 *
 * PR #7 (FE skeleton): read-only thunks.
 * PR #8 (FE): added dry-run + run polling thunks.
 * PR #10 (FE): adds the mutating endpoints — start migration, execute,
 *   set-phase, rollback, set-mirror-direction, take manual snapshot,
 *   pause-mirror / resume-mirror — plus per-action loading flags so multiple
 *   buttons can be in flight without conflict.
 * PR #12 (this PR, also folds PR #11 FE): adds observability data
 *   thunks — fetchStuckOutbox, fetchV2DeleteAttempts, fetchPhaseTransitions,
 *   fetchConflictHistory, fetchParityRuns, pollParityRun — plus the per-row
 *   outbox actions retryOutboxRow / skipOutboxRow with per-row loading and
 *   error maps so multiple rows can be in flight simultaneously.
 *
 * Endpoints called map 1:1 to BE routes in §5.1 of TASKS_V2_MIGRATION_PLAN.md.
 * Some endpoints (pause-run, resume-run, pause-mirror, resume-mirror) may not
 * yet exist on BE; thunks treat 404 as "not yet implemented" and return a
 * soft-skip flag rather than blowing up the UI.
 */
import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { callAdminApi } from '../../utils/apiCall';
import { updateError as setGlobalError } from './user';
// Coverage matrix is owned by its own slice (see tasksV2CoverageMatrix.js)
// to keep this main slice from ballooning. We import the thunk so the
// run-polling loop can refresh the matrix on terminal status — the BE
// payload changes after a succeeded dry run (projected_skips populates)
// and after execute (matrix becomes the locked-state record). Without
// this dispatch the operator has to click Recompute manually, and
// post-Execute the templates are locked so the matrix is stuck on
// pre-Execute data.
import { fetchCoverageMatrix } from './tasksV2CoverageMatrix';

const BASE = '/api/v1/management/tasks-migration';

// `callAdminApi` fires a generic "Something went wrong" toast on 5xx via the
// shared user slice. When we surface a 503 as a structured `setStorageOutage`
// banner, the generic toast becomes a duplicate. Call this immediately after
// a `setStorageOutage` dispatch to suppress the noise.
function _clearGlobalErrorToast() {
    dispatch(setGlobalError({ message: null, success: false }));
}

const POLL_INTERVAL_MS = 2000;
const TERMINAL_STATUSES = new Set(['succeeded', 'failed', 'cancelled']);

// Module-level handle to the polling timer so multiple components don't
// each spin up their own loop. Polling is gated by `state.activeRun` and
// only fires while document.visibilityState === 'visible'.
let _pollTimer = null;
let _pollRunId = null;

const ACTION_KEYS = [
    'startMigration',
    'execute',
    'setPhase',
    'rollback',
    'setMirrorDirection',
    'snapshot',
    'pauseMirror',
    'resumeMirror',
    'pauseRun',
    'resumeRun',
    'cancelRun',
    'parityCheck',
    'dryRun',
    // PR #13 — checkpoint management
    'archiveCheckpoint',
    'restoreCheckpoint',
    'deleteCheckpoint',
    'cleanupCheckpoints',
    // PR #14 — tasks-v2 PracticeFeature toggle from cockpit
    'setV2Flag',
    // PR #13 — V1/V2 feature-flag master control (cockpit-side mirror of
    // the per-practice PracticeFeature admin).
    'setFeatureFlags',
    // PR #15 — full reset of migration metadata (with optional V2 wipe)
    'resetMigration',
    // 2026-05-11 — orphaned-lock recovery from the Diagnostics panel.
    // The practice_lock context manager releases in its finally, but a
    // worker killed mid-request strands the lock for up to its TTL. The
    // BE endpoint requires confirm-by-practice-name to gate accidental use.
    'releaseLock',
    // PR #1 outbox controls — drain forward/back queues + recover stuck
    'drainOutbox',
    'recoverStuckOutbox',
    // PR #6 — drift drill-down: one-click force-mirror + confirm-dialog skip
    'forceMirror',
    'skipDriftRow',
    // PR — Beat heartbeat probe one-shot dispatch (synchronous parity check)
    'fireHeartbeat',
    // PR — Pre-flight bypass override (staging-only)
    'createPreflightBypass',
    'clearPreflightBypass',
    // PR — Force-advance via set-phase with ?force_no_soak=true
    'setPhaseWithSoakBypass',
    // [PR1] Tasks V2 Migration migration-mode toggle (CHECKLIST vs ONE_TO_ONE).
    'setMigrationMode',
    // [PR2] Tasks V2 Migration — V1 → V2 category mapper bulk-save.
    'saveCategoryRules',
];

const emptyActionMap = () => ACTION_KEYS.reduce((acc, k) => {
    acc[k] = false;
    return acc;
}, {});

const emptyErrorMap = () => ACTION_KEYS.reduce((acc, k) => {
    acc[k] = null;
    return acc;
}, {});

const initialState = {
    // List view
    practices: [],
    // Practice cockpit detail (keyed by practiceId)
    selectedPractice: null,
    practiceDetail: null,
    runs: [],
    checkpoints: [],
    webhookStatus: null,
    eta: null,
    userOverrides: [],
    // Loading / error
    loading: false,
    detailLoading: false,
    error: null,
    // PR #8 — Dry-run state
    activeDryRunByPractice: {}, // { [practiceId]: runId }
    activeRun: null, // { id, status, progress, last_log_lines, warnings, dry_run_preview, ... }
    dryRunStarting: false,
    dryRunBusy: null, // 423 MIGRATION_BUSY error message
    // PR #10 — Per-action loading + errors
    actionLoading: emptyActionMap(),
    actionErrors: emptyErrorMap(),
    // PR #12.6 — Transient success acknowledgement (action key + timestamp)
    lastActionSuccess: null,
    // PR #10 — Snapshot toast / acknowledgement
    lastSnapshotKey: null,
    // PR #10 — Currently-executing run (returned from POST execute)
    activeExecuteRunByPractice: {}, // { [practiceId]: runId }
    // PR #10 — Storage outage banner (set on HTTP 503 set-phase failure)
    storageOutage: null, // { code, message, action }
    // PR #12 — Observability panel data
    stuckOutbox: [],
    v2DeleteAttempts: [],
    phaseTransitions: [],
    conflictHistory: [],
    parityRuns: [],
    activeParityRun: null,        // single in-flight ParityCheckRun snapshot
    panelLoading: {
        stuckOutbox: false,
        v2DeleteAttempts: false,
        phaseTransitions: false,
        conflictHistory: false,
        parityRuns: false,
        diagnostics: false,
        preFlightHealth: false,
        phaseAdvanceEligibility: false,
        recommendedNextStep: false,
        // PR #6 — lazy per-row drift detail fetch
        parityDriftDetail: false,
        // PR #13 — V1/V2 feature-flag master control read.
        featureFlags: false,
        // PR — Redis broker live telemetry (Diagnose panel).
        redisTelemetry: false,
        // PR — Pre-flight bypass override snapshot read.
        preflightBypass: false,
        // Migration-safety banner: forward / rollback verdict aggregate.
        migrationSafety: false,
    },
    panelErrors: {
        stuckOutbox: null,
        v2DeleteAttempts: null,
        phaseTransitions: null,
        conflictHistory: null,
        parityRuns: null,
        diagnostics: null,
        preFlightHealth: null,
        phaseAdvanceEligibility: null,
        recommendedNextStep: null,
        // PR #6 — lazy per-row drift detail fetch
        parityDriftDetail: null,
        // PR #13 — V1/V2 feature-flag master control read.
        featureFlags: null,
        // PR — Redis broker live telemetry (Diagnose panel).
        redisTelemetry: null,
        // PR — Pre-flight bypass override snapshot read.
        preflightBypass: null,
        // Migration-safety banner.
        migrationSafety: null,
    },
    // PR #6 — Lazy drift drill-down cache. Keyed by `${bucket}:${id}` so
    // re-opens of the same row (and toggles between buckets) hit the cache.
    parityDriftDetail: {},
    // PR #5 FE — Recommended-next-step snapshot (cockpit CTA card). Computed
    // server-side so the FE doesn't drift; outbox-health-aware so the operator
    // never gets told to "Run Data Match" while the queue is still draining.
    recommendedNextStep: { data: null, lastFetchedAt: null },
    // Migration safety report — verdicts + blockers + warnings for the
    // forward / rollback decision. Drives the cockpit banner.
    // Shape (matches BE):
    //   {
    //     current_phase, current_canonical_side,
    //     tasks_v2_flag_enabled, tasks_v1_flag_enabled,
    //     mirror_paused, force_progressed, v4_plan_enabled,
    //     forward: { verdict, target_phase, blockers, warnings },
    //     rollback: { verdict, target_phase, would_lose, blockers, warnings },
    //     summary_text, checked_at,
    //   }
    migrationSafety: { data: null, lastFetchedAt: null },
    // PR #2 FE — Pre-flight health snapshot (cockpit gate). When
    // `data.overall_status === 'fail'` the four primary action thunks (start,
    // dryRun, execute, setPhase) are hard-blocked client-side AND BE returns
    // 412 PRE_FLIGHT_FAILED. There is no override path — operator must fix
    // the failing probe(s) and refetch.
    preFlightHealth: { data: null, lastFetchedAt: null },
    // PR #2 FE — Most-recent 412 PRE_FLIGHT_FAILED response from a primary
    // action attempt. Surfaces as a top-of-page Alert that points the operator
    // at the PreFlightHealthPanel and lists which probes are red.
    lastPreFlightBlock: null, // { failing_probes, probes, attempted_action } | null
    // PR #7 FE — Soak-time eligibility for the next forward phase. Refreshed
    // on cockpit mount + after relevant events (parity completes, phase
    // changes). Drives the per-option progress text + disabled state on the
    // Advance Phase menu.
    phaseAdvanceEligibility: { data: null }, // { current_phase, target_phase, eligible, unmet_conditions } | null
    // PR #7 FE — Most-recent 412 SOAK_TIME_NOT_MET response from set-phase.
    // Surfaces as a closable Alert in the cockpit listing the unmet
    // conditions so the operator knows why their click was refused.
    lastSoakTimeBlock: null, // { current_phase, target_phase, unmet_conditions } | null
    // PR #3 FE — Most-recent 412 EXECUTE_REQUIRES_SMOKE_PASS response from
    // execute. Surfaces as a top-of-page Alert similar to the pre-flight
    // block; resolution is "re-run dry run", not an override.
    lastSmokeBlock: null, // { smoke } | null
    // PR #13 — V1/V2 feature-flag master control. `data` mirrors the BE
    // GET response; `lastFeatureFlagBlock` captures structured 412 errors
    // so the FeatureFlagsPanel can render an inline alert without piggy-
    // backing on `actionErrors` (which is overwritten on retry).
    featureFlags: {
        data: null, // { v1_enabled, v2_enabled, v1_flag_exists, current_phase, allowed_combinations }
        lastFetchedAt: null,
    },
    lastFeatureFlagBlock: null, // { code, current_phase, requested_v1_enabled, requested_v2_enabled, reason, allowed_combinations? } | null
    // PR #15 — Diagnostics snapshot (Celery / broker / data health).
    diagnostics: null,
    // Per-row outbox action loading/errors so multiple rows can be in flight
    outboxActionLoading: {}, // { [outboxId]: bool }
    outboxActionErrors: {},  // { [outboxId]: string|null }
    // PR — Redis broker live telemetry (drives RedisDiagnose panel).
    // GET /redis-telemetry/ is NOT practice-scoped — telemetry is global.
    redisTelemetry: { data: null, lastFetchedAt: null },
    // PR — Pre-flight bypass override snapshot. Mirrors the BE GET response.
    // Drives the BypassActiveBanner and the PreflightBypassModal's
    // catalogue/active-state read.
    preflightBypass: { data: null, lastFetchedAt: null },
    // PR8.5 of v4 plan -- structured 409 response from rollback when
    // V2-only user activity exists. The RollbackConfirmDialog reads
    // this to render the activity summary + acknowledgment checkbox.
    // Cleared on rollback retry / success / cockpit unmount.
    lastRollbackDataLossBlock: null, // { activitySummary, since, detail, remediation } | null
    // Tasks V2 Migration v4 plan — PR2.5. Template selection wizard state.
    //   ambiguities: list returned by GET migration-template-ambiguities/
    //                (each item: {v1_group, treatment_item_id, candidates,
    //                candidate_template_ids, current_override_template_id,
    //                treatment_item_name})
    //   locked, lockedAt: lock state from the same GET response
    //   loading: while the GET is in flight
    //   saving: while POST migration-template-overrides/ is in flight
    //   lastSaveError: structured error from the POST
    //   lastSaveOk: success acknowledgement (timestamp + override count)
    // The endpoint returns 404 when TASKS_V2_MIGRATION_PREVIEW_V2 is OFF;
    // we treat 404 as "feature not available; keep ambiguities=null and
    // render nothing."
    templateSelection: {
        ambiguities: null,
        locked: false,
        lockedAt: null,
        loading: false,
        saving: false,
        lastSaveError: null,
        lastSaveOk: null,
        featureAvailable: true,  // flipped to false on 404
    },
    // [PR1] Tasks V2 Migration — per-practice migration-mode toggle.
    // ``value`` is the canonical 'checklist' | 'one_to_one' string the BE
    // returns from GET /practices/{id}/migration-mode/. Defaults to null
    // until the fetch resolves so the cockpit can render a skeleton without
    // assuming a mode (the BE owns the default — CHECKLIST today).
    //   setAt / setByUserEmail — audit metadata for the most recent flip.
    //   locked / lockReason   — 423 LOCKED block (ReminderTaskMap rows or
    //                           template_selection_locked_at set on BE).
    //   loading               — while GET is in flight.
    //   saving                — while POST is in flight.
    //   lastSaveError         — structured error from the POST.
    //   lastSaveOk            — {ts, mode} success ack for snackbar.
    migrationMode: {
        value: null,
        setAt: null,
        setByUserEmail: null,
        locked: false,
        lockReason: null,
        loading: false,
        saving: false,
        lastSaveError: null,
        lastSaveOk: null,
    },
    // [PR2] Tasks V2 Migration — V1 → V2 Category Map.
    //
    // Per-practice rules that override the system default
    // ``GROUP_TO_CATEGORY_MAP`` for the migration's category resolution.
    // The cockpit's CategoryMapperConfigure dialog mutates this block.
    //
    //   rules           — list of saved rules from the BE, or null when
    //                     unloaded (initial fetch in flight) so the trigger
    //                     card can show a skeleton without assuming "0 rules".
    //                     Each rule: { id, v1_group, v1_method, v2_category,
    //                                  created_at }.
    //   systemDefaults  — { v1_group: v2_category } the BE's
    //                     GROUP_TO_CATEGORY_MAP. Rendered in the "System
    //                     defaults" expander so the operator knows what
    //                     they're overriding.
    //   options         — choice lists for the three <Select>s on a row:
    //                       { groups: [...], methods: [...], categories: [...] }
    //   locked/lockReason/lockedAt — 423 LOCKED state once
    //                     template_selection_locked_at is set OR any
    //                     ReminderTaskMap rows exist for the practice.
    //   loading         — while GET is in flight.
    //   saving          — while POST is in flight.
    //   lastSaveError   — structured error from the POST.
    //   lastSaveOk      — {ts, count} success ack for the snackbar.
    //   featureAvailable — flipped to false on 404 so the trigger card
    //                     hides cleanly when the BE feature flag is off.
    categoryMap: {
        rules: null,
        systemDefaults: {},
        options: { groups: [], methods: [], categories: [] },
        locked: false,
        lockReason: null,
        lockedAt: null,
        loading: false,
        saving: false,
        lastSaveError: null,
        lastSaveOk: null,
        featureAvailable: true,
    },
};

const slice = createSlice({
    name: 'tasksV2Migration',
    initialState,
    reducers: {
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setDetailLoading(state, action) {
            state.detailLoading = action.payload;
        },
        setError(state, action) {
            state.error = action.payload;
        },
        setPractices(state, action) {
            state.practices = action.payload || [];
        },
        setSelectedPractice(state, action) {
            state.selectedPractice = action.payload;
        },
        setPracticeDetail(state, action) {
            const previousPhase = state.practiceDetail?.phase || null;
            state.practiceDetail = action.payload;
            // Stale-soak-block guard. ``lastSoakTimeBlock`` is a transient
            // payload from the most recent BLOCKED set-phase attempt — it
            // carries the operator's ``{current_phase, target_phase,
            // unmet_conditions}`` so the cockpit can show "Soak gate is
            // blocking advance to X" until they retry. The slice doesn't
            // auto-clear it on a phase change because it's set/cleared by
            // explicit action types (see ``setLastSoakTimeBlock`` /
            // ``clearLastSoakTimeBlock``). Without this guard, after the
            // cockpit refreshes ``practiceDetail`` and the phase has moved
            // (e.g. operator advanced via the BE, or a rollback happened),
            // the stale block keeps rendering "Soak gate is blocking
            // advance to <old-target>" even though the practice is no
            // longer at the block's ``current_phase``. Surface symptom on
            // 2026-05-13: cockpit showed wizard at step 4 (``dual_canonical``)
            // with a soak block referring to advance-from-``dual_canonical``
            // while ``migration-safety`` correctly reported the new phase.
            //
            // Clear ``lastSoakTimeBlock`` only when the practice's phase
            // has materially changed AND no longer matches the block's
            // ``current_phase``. Don't clear on first-load (previousPhase
            // is null) so a freshly-rehydrated session keeps any persisted
            // block until the operator retries.
            const newPhase = action.payload?.phase || null;
            const block = state.lastSoakTimeBlock;
            if (
                previousPhase
                && newPhase
                && previousPhase !== newPhase
                && block
                && block.current_phase
                && block.current_phase !== newPhase
            ) {
                state.lastSoakTimeBlock = null;
            }
        },
        setRuns(state, action) {
            state.runs = action.payload || [];
        },
        setCheckpoints(state, action) {
            state.checkpoints = action.payload || [];
        },
        setWebhookStatus(state, action) {
            state.webhookStatus = action.payload;
        },
        setEta(state, action) {
            state.eta = action.payload;
        },
        setUserOverrides(state, action) {
            state.userOverrides = action.payload || [];
        },
        // PR8.5 of v4 plan -- rollback data-loss block reducers.
        setLastRollbackDataLossBlock(state, action) {
            state.lastRollbackDataLossBlock = action.payload || null;
        },
        clearLastRollbackDataLossBlock(state) {
            state.lastRollbackDataLossBlock = null;
        },
        // Tasks V2 Migration v4 plan — PR2.5 wizard reducers.
        setTemplateSelectionLoading(state, action) {
            state.templateSelection.loading = action.payload;
        },
        setTemplateSelectionSnapshot(state, action) {
            const { ambiguities, locked, lockedAt } = action.payload || {};
            state.templateSelection.ambiguities = ambiguities ?? [];
            state.templateSelection.locked = !!locked;
            state.templateSelection.lockedAt = lockedAt ?? null;
            state.templateSelection.featureAvailable = true;
        },
        setTemplateSelectionUnavailable(state) {
            // Flag-OFF response (HTTP 404). Render the panel as null.
            state.templateSelection.ambiguities = null;
            state.templateSelection.locked = false;
            state.templateSelection.lockedAt = null;
            state.templateSelection.featureAvailable = false;
        },
        setTemplateOverridesSaving(state, action) {
            state.templateSelection.saving = action.payload;
        },
        setTemplateOverridesError(state, action) {
            state.templateSelection.lastSaveError = action.payload;
        },
        setTemplateOverridesSuccess(state, action) {
            state.templateSelection.lastSaveOk = action.payload;
            state.templateSelection.lastSaveError = null;
        },
        // [PR1] Migration-mode toggle reducers ----------------------------
        setMigrationModeLoading(state, action) {
            state.migrationMode.loading = !!action.payload;
        },
        setMigrationModeValue(state, action) {
            const p = action.payload || {};
            // Accept partial payloads (e.g. when the POST returns only the
            // new mode + audit fields and we want to keep loading/saving
            // flags untouched).
            if (Object.prototype.hasOwnProperty.call(p, 'value')) {
                state.migrationMode.value = p.value ?? null;
            }
            if (Object.prototype.hasOwnProperty.call(p, 'setAt')) {
                state.migrationMode.setAt = p.setAt ?? null;
            }
            if (Object.prototype.hasOwnProperty.call(p, 'setByUserEmail')) {
                state.migrationMode.setByUserEmail = p.setByUserEmail ?? null;
            }
            if (Object.prototype.hasOwnProperty.call(p, 'locked')) {
                state.migrationMode.locked = !!p.locked;
            }
            if (Object.prototype.hasOwnProperty.call(p, 'lockReason')) {
                state.migrationMode.lockReason = p.lockReason ?? null;
            }
        },
        setMigrationModeSaving(state, action) {
            state.migrationMode.saving = !!action.payload;
        },
        setMigrationModeError(state, action) {
            state.migrationMode.lastSaveError = action.payload || null;
        },
        setMigrationModeSuccess(state, action) {
            state.migrationMode.lastSaveOk = action.payload || null;
            state.migrationMode.lastSaveError = null;
        },
        // [PR2] Category-map reducers ------------------------------------
        setCategoryMapLoading(state, action) {
            state.categoryMap.loading = !!action.payload;
        },
        setCategoryMapData(state, action) {
            const p = action.payload || {};
            state.categoryMap.rules = Array.isArray(p.rules) ? p.rules : [];
            state.categoryMap.systemDefaults = p.systemDefaults || {};
            state.categoryMap.options = {
                groups: p.options?.groups || [],
                methods: p.options?.methods || [],
                categories: p.options?.categories || [],
            };
            state.categoryMap.locked = !!p.locked;
            state.categoryMap.lockReason = p.lockReason ?? null;
            state.categoryMap.lockedAt = p.lockedAt ?? null;
            state.categoryMap.featureAvailable = true;
        },
        setCategoryMapSaving(state, action) {
            state.categoryMap.saving = !!action.payload;
        },
        setCategoryMapError(state, action) {
            state.categoryMap.lastSaveError = action.payload || null;
        },
        setCategoryMapSuccess(state, action) {
            state.categoryMap.lastSaveOk = action.payload || null;
            state.categoryMap.lastSaveError = null;
        },
        setCategoryMapUnavailable(state) {
            // BE feature flag OFF (HTTP 404). Render the trigger card as null
            // — same posture as setTemplateSelectionUnavailable.
            state.categoryMap.rules = null;
            state.categoryMap.systemDefaults = {};
            state.categoryMap.options = { groups: [], methods: [], categories: [] };
            state.categoryMap.locked = false;
            state.categoryMap.lockReason = null;
            state.categoryMap.lockedAt = null;
            state.categoryMap.featureAvailable = false;
        },
        clearPracticeContext(state) {
            state.practiceDetail = null;
            state.runs = [];
            state.checkpoints = [];
            state.webhookStatus = null;
            state.eta = null;
            state.userOverrides = [];
            // PR2.5 — wizard state is per-practice; reset on navigation.
            state.templateSelection = {
                ambiguities: null,
                locked: false,
                lockedAt: null,
                loading: false,
                saving: false,
                lastSaveError: null,
                lastSaveOk: null,
                featureAvailable: true,
            };
            // [PR1] migration-mode is per-practice — reset on navigation so a
            // previous practice's CHECKLIST/ONE_TO_ONE value doesn't briefly
            // render before the new fetch lands.
            state.migrationMode = {
                value: null,
                setAt: null,
                setByUserEmail: null,
                locked: false,
                lockReason: null,
                loading: false,
                saving: false,
                lastSaveError: null,
                lastSaveOk: null,
            };
            // [PR2] category-map is per-practice — reset on navigation so a
            // previous practice's rules/options don't briefly render before
            // the new fetch lands.
            state.categoryMap = {
                rules: null,
                systemDefaults: {},
                options: { groups: [], methods: [], categories: [] },
                locked: false,
                lockReason: null,
                lockedAt: null,
                loading: false,
                saving: false,
                lastSaveError: null,
                lastSaveOk: null,
                featureAvailable: true,
            };
            // PR #12 — also reset observability panel buffers so the new
            // practice doesn't show stale rows from the previous one.
            state.stuckOutbox = [];
            state.v2DeleteAttempts = [];
            state.phaseTransitions = [];
            state.conflictHistory = [];
            state.parityRuns = [];
            state.activeParityRun = null;
            state.outboxActionLoading = {};
            state.outboxActionErrors = {};
            // PR #12.6 — also reset per-action errors and the success flash so
            // they don't leak across practice navigations (e.g. a stale
            // "setPhase — Migration not started" toast on cockpit mount).
            state.actionErrors = emptyErrorMap();
            state.lastActionSuccess = null;
            // Clear activeRun and storageOutage so the cockpit doesn't render
            // "Execute in progress" / red banners for a previous practice's
            // state, or for a run that was wiped by seed_migration_scenarios.
            // The auto-resume effect on the cockpit will reseed activeRun
            // from `runs` once fetchPracticeRuns completes, so legitimate
            // in-flight runs are picked back up.
            state.activeRun = null;
            state.storageOutage = null;
            // PR #2 FE — drop the previous practice's pre-flight snapshot so
            // the panel doesn't briefly render stale probe results before the
            // new fetch lands.
            state.preFlightHealth = { data: null, lastFetchedAt: null };
            state.lastPreFlightBlock = null;
            // PR #7 FE — same hygiene for the soak-time gate state.
            state.phaseAdvanceEligibility = { data: null };
            state.lastSoakTimeBlock = null;
            // PR #3 FE — clear the smoke-block too on practice navigation.
            state.lastSmokeBlock = null;
            // PR #5 FE — drop the previous practice's recommendation so the
            // CTA card doesn't briefly render a stale step on cockpit mount.
            state.recommendedNextStep = { data: null, lastFetchedAt: null };
            state.migrationSafety = { data: null, lastFetchedAt: null };
            // PR #6 FE — drift drill-down cache. Cleared on practice change so
            // a row id from practice A can't show V1 content from practice B.
            state.parityDriftDetail = {};
            // PR #13 FE — drop the previous practice's feature-flag snapshot
            // and any stashed phase-invalid block.
            state.featureFlags = { data: null, lastFetchedAt: null };
            state.lastFeatureFlagBlock = null;
            // PR — drop the previous practice's bypass snapshot. (Redis
            // telemetry is global so it intentionally survives.)
            state.preflightBypass = { data: null, lastFetchedAt: null };
        },
        // PR #8 — Dry-run reducers
        setDryRunStarting(state, action) {
            state.dryRunStarting = !!action.payload;
        },
        setDryRunBusy(state, action) {
            state.dryRunBusy = action.payload || null;
        },
        setActiveDryRun(state, action) {
            const { practiceId, runId } = action.payload || {};
            if (!practiceId || !runId) return;
            state.activeDryRunByPractice[practiceId] = runId;
        },
        clearActiveDryRunForPractice(state, action) {
            const practiceId = action.payload;
            if (practiceId == null) return;
            delete state.activeDryRunByPractice[practiceId];
        },
        setActiveRun(state, action) {
            // Merge rather than replace so fields seeded at start (`mode`,
            // `id`) survive subsequent poll snapshots, which only carry
            // {status, progress, last_log_lines, warnings, dry_run_preview}.
            // Without this merge the cockpit's "Dry run in progress" /
            // "Execute in progress" pickStep loses the mode after the
            // first poll tick.
            const next = action.payload || null;
            if (!next) {
                state.activeRun = null;
                return;
            }
            state.activeRun = { ...(state.activeRun || {}), ...next };
        },
        clearActiveRun(state) {
            state.activeRun = null;
        },
        // PR #10 — action loading
        setActionLoading(state, action) {
            const { key, value } = action.payload || {};
            if (!key) return;
            state.actionLoading[key] = !!value;
        },
        setActionError(state, action) {
            const { key, error } = action.payload || {};
            if (!key) return;
            state.actionErrors[key] = error || null;
        },
        clearActionError(state, action) {
            const key = action.payload;
            if (!key) return;
            state.actionErrors[key] = null;
        },
        clearAllActionErrors(state) {
            state.actionErrors = emptyErrorMap();
        },
        setLastActionSuccess(state, action) {
            const key = action.payload;
            if (!key) {
                state.lastActionSuccess = null;
                return;
            }
            state.lastActionSuccess = { key, at: Date.now() };
        },
        clearLastActionSuccess(state) {
            state.lastActionSuccess = null;
        },
        setLastSnapshotKey(state, action) {
            state.lastSnapshotKey = action.payload || null;
        },
        setActiveExecuteRun(state, action) {
            const { practiceId, runId } = action.payload || {};
            if (!practiceId || !runId) return;
            state.activeExecuteRunByPractice[practiceId] = runId;
        },
        clearActiveExecuteRunForPractice(state, action) {
            const practiceId = action.payload;
            if (practiceId == null) return;
            delete state.activeExecuteRunByPractice[practiceId];
        },
        setStorageOutage(state, action) {
            state.storageOutage = action.payload || null;
        },
        clearStorageOutage(state) {
            state.storageOutage = null;
        },
        // PR #12 — Observability panel reducers
        setPanelLoading(state, action) {
            const { key, value } = action.payload || {};
            if (!key || !(key in state.panelLoading)) return;
            state.panelLoading[key] = !!value;
        },
        setPanelError(state, action) {
            const { key, error } = action.payload || {};
            if (!key || !(key in state.panelErrors)) return;
            state.panelErrors[key] = error || null;
        },
        setDiagnostics(state, action) {
            state.diagnostics = action.payload || null;
        },
        setStuckOutbox(state, action) {
            state.stuckOutbox = Array.isArray(action.payload) ? action.payload : [];
        },
        setV2DeleteAttempts(state, action) {
            state.v2DeleteAttempts = Array.isArray(action.payload) ? action.payload : [];
        },
        setPhaseTransitionsList(state, action) {
            state.phaseTransitions = Array.isArray(action.payload) ? action.payload : [];
        },
        setConflictHistory(state, action) {
            state.conflictHistory = Array.isArray(action.payload) ? action.payload : [];
        },
        setParityRuns(state, action) {
            state.parityRuns = Array.isArray(action.payload) ? action.payload : [];
        },
        setActiveParityRun(state, action) {
            state.activeParityRun = action.payload || null;
        },
        clearActiveParityRun(state) {
            state.activeParityRun = null;
        },
        setOutboxActionLoading(state, action) {
            const { id, value } = action.payload || {};
            if (id == null) return;
            if (value) {
                state.outboxActionLoading[id] = true;
            } else {
                delete state.outboxActionLoading[id];
            }
        },
        setOutboxActionError(state, action) {
            const { id, error } = action.payload || {};
            if (id == null) return;
            if (error) {
                state.outboxActionErrors[id] = error;
            } else {
                delete state.outboxActionErrors[id];
            }
        },
        removeStuckOutboxRow(state, action) {
            const id = action.payload;
            if (id == null) return;
            state.stuckOutbox = state.stuckOutbox.filter((r) => r.id !== id);
        },
        // PR #2 FE — Pre-flight health reducers
        setPreFlightHealth(state, action) {
            state.preFlightHealth.data = action.payload || null;
            state.preFlightHealth.lastFetchedAt = new Date().toISOString();
        },
        // PR #5 FE — Recommended-next-step reducer.
        setRecommendedNextStep(state, action) {
            state.recommendedNextStep.data = action.payload || null;
            state.recommendedNextStep.lastFetchedAt = new Date().toISOString();
        },
        // Migration safety banner — aggregated forward/rollback verdict.
        // Refreshed on cockpit mount + after any phase / parity / outbox
        // event so the banner can never lag behind the BE state.
        setMigrationSafety(state, action) {
            state.migrationSafety.data = action.payload || null;
            state.migrationSafety.lastFetchedAt = new Date().toISOString();
        },
        setLastPreFlightBlock(state, action) {
            state.lastPreFlightBlock = action.payload || null;
        },
        clearLastPreFlightBlock(state) {
            state.lastPreFlightBlock = null;
        },
        // PR #6 FE — Drift drill-down cache reducer.
        setParityDriftDetail(state, action) {
            const { key, data } = action.payload || {};
            if (!key) return;
            state.parityDriftDetail[key] = data || null;
        },
        clearParityDriftDetail(state, action) {
            const key = action.payload;
            if (!key) {
                state.parityDriftDetail = {};
                return;
            }
            delete state.parityDriftDetail[key];
        },
        // PR #7 FE — Soak-time eligibility reducers
        setPhaseAdvanceEligibility(state, action) {
            state.phaseAdvanceEligibility.data = action.payload || null;
        },
        setLastSoakTimeBlock(state, action) {
            state.lastSoakTimeBlock = action.payload || null;
        },
        clearLastSoakTimeBlock(state) {
            state.lastSoakTimeBlock = null;
        },
        // PR #3 FE — Smoke-block reducers
        setLastSmokeBlock(state, action) {
            state.lastSmokeBlock = action.payload || null;
        },
        clearLastSmokeBlock(state) {
            state.lastSmokeBlock = null;
        },
        // PR #13 FE — Feature-flag master control reducers.
        setFeatureFlagsState(state, action) {
            state.featureFlags.data = action.payload || null;
            state.featureFlags.lastFetchedAt = new Date().toISOString();
        },
        setLastFeatureFlagBlock(state, action) {
            state.lastFeatureFlagBlock = action.payload || null;
        },
        clearLastFeatureFlagBlock(state) {
            state.lastFeatureFlagBlock = null;
        },
        // PR — Redis broker telemetry reducer (RedisDiagnose panel).
        setRedisTelemetry(state, action) {
            const payload = action.payload || {};
            state.redisTelemetry.data = payload.data || null;
            state.redisTelemetry.lastFetchedAt = payload.lastFetchedAt
                || new Date().toISOString();
        },
        // PR — Pre-flight bypass override reducer (banner + modal).
        setPreflightBypass(state, action) {
            const payload = action.payload || {};
            state.preflightBypass.data = payload.data || null;
            state.preflightBypass.lastFetchedAt = payload.lastFetchedAt
                || new Date().toISOString();
        },
    },
});

export default slice.reducer;
export const {
    setSelectedPractice,
    clearPracticeContext,
    setActiveDryRun,
    clearActiveDryRunForPractice,
    setActiveRun,
    clearActiveRun,
    clearActionError,
    clearAllActionErrors,
    clearLastActionSuccess,
    clearStorageOutage,
    setActiveExecuteRun,
    clearActiveExecuteRunForPractice,
    clearActiveParityRun,
    clearLastPreFlightBlock,
    clearLastSoakTimeBlock,
    clearLastSmokeBlock,
    clearLastFeatureFlagBlock,
    setTemplateOverridesError,
    setTemplateOverridesSuccess,
    clearLastRollbackDataLossBlock,
    // [PR1] migration-mode reducers exported for direct dispatch + tests.
    setMigrationModeLoading,
    setMigrationModeValue,
    setMigrationModeSaving,
    setMigrationModeError,
    setMigrationModeSuccess,
    // [PR2] category-map reducers exported for direct dispatch + tests.
    setCategoryMapLoading,
    setCategoryMapData,
    setCategoryMapSaving,
    setCategoryMapError,
    setCategoryMapSuccess,
    setCategoryMapUnavailable,
} = slice.actions;

// --------------- Thunks ---------------

/**
 * PR #2 FE — Pre-flight health.
 *
 * Helper that recognises an HTTP 412 PRE_FLIGHT_FAILED response from the
 * cockpit's four primary action endpoints (start-migration, dry-run, execute,
 * set-phase). When recognised, captures the failing-probe payload into
 * `lastPreFlightBlock` and refreshes `preFlightHealth.data` from the embedded
 * `probes` list so the operator sees the same red rows the BE just refused on.
 *
 * Returns true when the response was a pre-flight block (so the caller can
 * skip its normal error-surfacing path), false otherwise.
 */
function _maybeCapturePreFlightBlock(resp, attemptedAction) {
    if (!resp || resp.status !== 412) return false;
    const data = resp.data || {};
    if (data.code !== 'PRE_FLIGHT_FAILED') return false;
    dispatch(slice.actions.setLastPreFlightBlock({
        failing_probes: Array.isArray(data.failing_probes) ? data.failing_probes : [],
        probes: Array.isArray(data.probes) ? data.probes : [],
        attempted_action: attemptedAction || null,
    }));
    // Re-seed the panel snapshot so the gate UI matches what BE just rejected on.
    if (Array.isArray(data.probes)) {
        dispatch(slice.actions.setPreFlightHealth({
            overall_status: 'fail',
            probes: data.probes,
            checked_at: new Date().toISOString(),
        }));
    }
    return true;
}

/**
 * PR #7 FE — Soak-time block capture.
 *
 * Recognises an HTTP 412 SOAK_TIME_NOT_MET response from `set-phase` and
 * stashes the unmet-conditions payload in `lastSoakTimeBlock` so the cockpit
 * can render a closable warning Alert above the action panel. Also re-seeds
 * `phaseAdvanceEligibility.data` so the Advance Phase menu reflects the new
 * deltas without waiting for the next polling tick.
 *
 * Returns true if the response was handled, false otherwise.
 */
function _maybeCaptureSoakTimeBlock(resp) {
    if (!resp || resp.status !== 412) return false;
    const data = resp.data || {};
    if (data.code !== 'SOAK_TIME_NOT_MET') return false;
    const payload = {
        current_phase: data.current_phase || null,
        target_phase: data.target_phase || null,
        unmet_conditions: Array.isArray(data.unmet_conditions)
            ? data.unmet_conditions : [],
    };
    dispatch(slice.actions.setLastSoakTimeBlock(payload));
    dispatch(slice.actions.setPhaseAdvanceEligibility({
        ...payload,
        eligible: false,
    }));
    return true;
}

/**
 * PR #3 FE — Smoke-pass block capture.
 *
 * Recognises an HTTP 412 EXECUTE_REQUIRES_SMOKE_PASS response from `execute`
 * and stashes the smoke payload in `lastSmokeBlock` so the cockpit can render
 * a top-of-page Alert pointing the operator at the dry-run smoke timeline.
 * Resolution is "re-run the dry run" — there is no override path.
 *
 * Returns true if the response was handled, false otherwise.
 */
function _maybeCaptureSmokeBlock(resp) {
    if (!resp || resp.status !== 412) return false;
    const data = resp.data || {};
    if (data.code !== 'EXECUTE_REQUIRES_SMOKE_PASS') return false;
    dispatch(slice.actions.setLastSmokeBlock({
        smoke: data.smoke || null,
    }));
    return true;
}

function unwrapList(data) {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    if (Array.isArray(data.results)) return data.results;
    return [];
}

function extractDetail(data, fallback) {
    if (!data) return fallback;
    if (typeof data === 'string') return data;
    return data.detail || data.error || data.message || fallback;
}

export function fetchTasksMigrationPractices() {
    return async () => {
        try {
            dispatch(slice.actions.setLoading(true));
            dispatch(slice.actions.setError(null));
            const response = await callAdminApi(`${BASE}/practices/`);
            if (response.status === 200) {
                dispatch(slice.actions.setPractices(unwrapList(response.data)));
            } else {
                dispatch(slice.actions.setError(`Failed to load practices (HTTP ${response.status})`));
                dispatch(slice.actions.setPractices([]));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error?.message || String(error)));
        } finally {
            dispatch(slice.actions.setLoading(false));
        }
    };
}

export function fetchPracticeDetail(practiceId) {
    return async () => {
        if (!practiceId) return;
        try {
            dispatch(slice.actions.setDetailLoading(true));
            dispatch(slice.actions.setError(null));
            const response = await callAdminApi(`${BASE}/practices/${practiceId}/`);
            if (response.status === 200) {
                dispatch(slice.actions.setPracticeDetail(response.data));
            } else {
                dispatch(slice.actions.setPracticeDetail(null));
                dispatch(slice.actions.setError(`Failed to load practice detail (HTTP ${response.status})`));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error?.message || String(error)));
        } finally {
            dispatch(slice.actions.setDetailLoading(false));
        }
    };
}

export function fetchPracticeRuns(practiceId) {
    return async () => {
        if (!practiceId) return;
        try {
            const response = await callAdminApi(`${BASE}/practices/${practiceId}/runs/`);
            if (response.status === 200) {
                dispatch(slice.actions.setRuns(unwrapList(response.data)));
            } else {
                dispatch(slice.actions.setRuns([]));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error?.message || String(error)));
            dispatch(slice.actions.setRuns([]));
        }
    };
}

export function fetchCheckpoints(practiceId) {
    return async () => {
        if (!practiceId) return;
        try {
            const response = await callAdminApi(`${BASE}/practices/${practiceId}/checkpoints/`);
            if (response.status === 200) {
                dispatch(slice.actions.setCheckpoints(unwrapList(response.data)));
            } else {
                // 404 is expected if practice has never had a PracticeMigrationState row.
                dispatch(slice.actions.setCheckpoints([]));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error?.message || String(error)));
            dispatch(slice.actions.setCheckpoints([]));
        }
    };
}

export function fetchWebhookStatus(practiceId) {
    return async () => {
        if (!practiceId) return;
        try {
            const response = await callAdminApi(`${BASE}/practices/${practiceId}/webhook-status/`);
            if (response.status === 200) {
                dispatch(slice.actions.setWebhookStatus(response.data));
            } else {
                dispatch(slice.actions.setWebhookStatus(null));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error?.message || String(error)));
            dispatch(slice.actions.setWebhookStatus(null));
        }
    };
}

export function fetchEta(practiceId) {
    return async () => {
        if (!practiceId) return;
        try {
            const response = await callAdminApi(`${BASE}/practices/${practiceId}/eta/`);
            if (response.status === 200) {
                dispatch(slice.actions.setEta(response.data));
            } else {
                dispatch(slice.actions.setEta(null));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error?.message || String(error)));
            dispatch(slice.actions.setEta(null));
        }
    };
}

export function fetchUserOverrides(practiceId) {
    return async () => {
        if (!practiceId) return;
        try {
            const response = await callAdminApi(`${BASE}/practices/${practiceId}/user-overrides/`);
            if (response.status === 200) {
                dispatch(slice.actions.setUserOverrides(unwrapList(response.data)));
            } else {
                dispatch(slice.actions.setUserOverrides([]));
            }
        } catch (error) {
            dispatch(slice.actions.setError(error?.message || String(error)));
            dispatch(slice.actions.setUserOverrides([]));
        }
    };
}

// --------------- PR #8 thunks: dry-run + polling ---------------

/**
 * runDryRun — POSTs to /practices/{practiceId}/dry-run/ and starts polling.
 *
 * BE endpoint returns `{run_id, status: 'pending'}` on 202/200 success, or
 * 423 `MIGRATION_BUSY` if another run is in flight for the same practice.
 *
 * On success, stores `runId` under `activeDryRunByPractice[practiceId]` and
 * begins the 2s polling loop via `pollRun`. The caller (modal/cockpit) is
 * responsible for stopping polling on unmount via `stopRunPolling`.
 */
export function runDryRun(practiceId) {
    return async () => {
        if (!practiceId) return null;
        try {
            dispatch(slice.actions.setDryRunStarting(true));
            dispatch(slice.actions.setDryRunBusy(null));
            // PR #12.7 FE — clear any stale per-action error so the alert pile
            // doesn't show a previous run's failure when starting a new one.
            dispatch(slice.actions.setActionError({ key: 'dryRun', error: null }));
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/dry-run/`,
                'POST',
                {},
            );
            if (response.status === 200 || response.status === 201 || response.status === 202) {
                const runId = response.data?.run_id || response.data?.id;
                if (!runId) {
                    dispatch(slice.actions.setActionError({
                        key: 'dryRun',
                        error: 'Dry-run started but no run_id returned',
                    }));
                    return response;
                }
                dispatch(slice.actions.setActiveDryRun({ practiceId, runId }));
                // Seed activeRun with the pending status from the POST response
                // so the UI can switch to "running" state immediately.
                dispatch(slice.actions.setActiveRun({
                    id: runId,
                    status: response.data?.status || 'pending',
                    progress: { done: 0, total: 0, percent: 0, eta_seconds: null },
                    last_log_lines: [],
                    warnings: [],
                    dry_run_preview: [],
                }));
                // PR #12.7 FE — match performAction semantics so the success
                // snackbar fires and the run-history list refreshes on
                // terminal poll status (see startRunPolling).
                dispatch(slice.actions.setLastActionSuccess('dryRun'));
                dispatch(startRunPolling(runId, practiceId));
            } else if (response.status === 423) {
                const detail = extractDetail(
                    response.data,
                    'Another migration operation is in flight for this practice (HTTP 423).',
                );
                dispatch(slice.actions.setDryRunBusy(detail));
            } else if (_maybeCapturePreFlightBlock(response, 'dryRun')) {
                // PR #2 FE — pre-flight gate handled at the top-level Alert;
                // skip the per-action error toast.
            } else {
                const detail = extractDetail(
                    response.data,
                    `Failed to start dry-run (HTTP ${response.status})`,
                );
                dispatch(slice.actions.setActionError({ key: 'dryRun', error: detail }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setActionError({ key: 'dryRun', error: message }));
            return { ok: false, status: 0, data: null, error };
        } finally {
            dispatch(slice.actions.setDryRunStarting(false));
        }
    };
}

/**
 * pollRun — GETs /runs/{runId}/ once. Stores the snapshot in `activeRun`.
 * Returns the run object so the caller can stop polling on terminal status.
 *
 * Returns a special `{__gone: true}` sentinel on 404 so the polling loop
 * can recognise that the run was deleted (e.g. via seed_migration_scenarios
 * --scenario clean) and stop immediately instead of hammering the
 * already-dead endpoint forever.
 */
export function pollRun(runId) {
    return async () => {
        if (!runId) return null;
        try {
            const response = await callAdminApi(`${BASE}/runs/${runId}/`);
            if (response.status === 200 && response.data) {
                dispatch(slice.actions.setActiveRun(response.data));
                return response.data;
            }
            if (response.status === 404) {
                // Run was deleted out from under us. Clear local state so
                // the auto-resume effect doesn't try to revive it, and
                // signal the caller to terminate the polling loop.
                dispatch(slice.actions.setActiveRun(null));
                return { __gone: true };
            }
            // Other non-200 — soft error, keep polling. (Could be a transient
            // 5xx from a backend reboot.)
            return null;
        } catch (error) {
            // Network blips happen — don't kill the loop, just log.
            // eslint-disable-next-line no-console
            console.warn('[tasksV2Migration.pollRun] poll error', error);
            return null;
        }
    };
}

/**
 * startRunPolling — kicks off a 2s polling loop for the given runId. Polling
 * pauses while `document.visibilityState !== 'visible'` and stops on terminal
 * status (succeeded / failed / cancelled).
 *
 * If `practiceId` is supplied, the run-history list (`fetchPracticeRuns`) is
 * refreshed once on the terminal tick so the cockpit's Run history table
 * picks up the freshly-completed dry-run / execute row without the user
 * having to hit Refresh manually.
 */
// Hard cap on how long a single polling loop runs before auto-stopping. A
// stuck or orphaned run shouldn't keep firing requests for hours. 10 min
// is generous for normal dry-run / execute durations.
const _POLL_MAX_DURATION_MS = 10 * 60 * 1000;
// Number of consecutive null/error ticks before we give up.
const _POLL_MAX_CONSECUTIVE_ERRORS = 30;

export function startRunPolling(runId, practiceId) {
    return () => {
        if (!runId) return;
        // Restart cleanly if a different run was already being polled.
        if (_pollTimer && _pollRunId !== runId) {
            clearInterval(_pollTimer);
            _pollTimer = null;
        }
        if (_pollTimer) return;
        _pollRunId = runId;

        const startedAt = Date.now();
        let consecutiveErrors = 0;

        const stopLoop = (reasonForLog) => {
            if (_pollTimer) {
                clearInterval(_pollTimer);
                _pollTimer = null;
                _pollRunId = null;
            }
            if (reasonForLog) {
                // eslint-disable-next-line no-console
                console.info(`[tasksV2Migration.startRunPolling] stopped: ${reasonForLog}`);
            }
        };

        const tick = async () => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
                return; // skip this tick — picks up again when tab visible
            }
            // Hard cap: don't poll forever even if BE keeps responding.
            if (Date.now() - startedAt > _POLL_MAX_DURATION_MS) {
                stopLoop('max-duration');
                return;
            }
            const run = await dispatch(pollRun(runId));

            // 404 — the run was deleted (e.g. via seed_migration_scenarios
            // clean). Stop immediately so we don't hammer a dead endpoint.
            if (run && run.__gone) {
                stopLoop('run-gone-404');
                return;
            }

            // Null = network/transient. Track consecutive failures.
            if (run == null) {
                consecutiveErrors += 1;
                if (consecutiveErrors >= _POLL_MAX_CONSECUTIVE_ERRORS) {
                    stopLoop('too-many-errors');
                }
                return;
            }
            consecutiveErrors = 0;

            if (run.status && TERMINAL_STATUSES.has(run.status)) {
                stopLoop('terminal-status');
                if (practiceId) {
                    // Fire-and-forget refresh so Run history, the HEALTHY
                    // header chips, Recommended Next Step, and Checkpoint
                    // inventory all reflect the post-run state without the
                    // user pressing Refresh.
                    dispatch(fetchPracticeRuns(practiceId));
                    dispatch(fetchPracticeDetail(practiceId));
                    dispatch(fetchCheckpoints(practiceId));
                    // Refresh the coverage matrix only on succeeded
                    // dry-run / execute. Reasoning:
                    //   - dry_run: projected_skip_count is sourced from
                    //     the latest succeeded MODE_DRY_RUN
                    //     (tasks_migration_coverage_views.py:233-310);
                    //     without this dispatch the FE keeps the stale
                    //     pre-dry-run snapshot.
                    //   - execute: post-Execute the templates lock; the
                    //     matrix becomes the canonical record of what
                    //     migrated, so we want it fresh.
                    //   - Skipped for parity-check / rollback / failed /
                    //     cancelled — those don't change matrix data and
                    //     a cold-cache fetch can be slow on large
                    //     practices (BE walks the resolver per bucket).
                    if (run.status === 'succeeded'
                        && (run.mode === 'dry_run' || run.mode === 'execute')) {
                        dispatch(fetchCoverageMatrix(practiceId, { fresh: true }));
                    }
                }
            }
        };

        // Fire one immediate tick so the modal doesn't sit empty for 2s.
        tick();
        _pollTimer = setInterval(tick, POLL_INTERVAL_MS);
    };
}

/**
 * stopRunPolling — terminates the polling loop. Safe to call multiple times.
 * Does NOT clear `activeRun` — the snapshot stays so reopening the modal
 * resumes display per §5.2.3 background-safe rule.
 */
export function stopRunPolling() {
    return () => {
        if (_pollTimer) {
            clearInterval(_pollTimer);
            _pollTimer = null;
            _pollRunId = null;
        }
    };
}

// --------------- PR #10 thunks: mutating actions ---------------

// Default axios timeout for mutating action endpoints. The cockpit's
// shared client (`callAdminApi`) defaults to 10s — fine for a normal
// read but too tight for these endpoints, every one of which runs
// `run_pre_flight_probes(practice)` inside `practice_lock` plus a
// checkpoint write + phase/state DB writes + audit log. On staging the
// Celery `inspect()` broadcasts inside pre-flight reliably consume
// ~4s, leaving < 6s for the rest of the action — set-phase started
// being killed at 9.78s with HTTP 0, which leaves the BE work mid-flight
// (no `@transaction.atomic` wraps set_phase) and confuses operators
// about whether the phase actually advanced.
//
// 30s matches the precedent set by `fetchDiagnostics` lower in this
// file (which carries a longer comment explaining the same class of
// bug). The pre-flight cache shipped on 2026-05-11 reduces the
// frequency of hitting the slow path on the read endpoints, but
// mutating endpoints MUST keep calling `run_pre_flight_probes`
// directly (caching the safety gate would let work get enqueued onto
// a dead broker), so the timeout bump is the right lever for them.
const ACTION_TIMEOUT_MS = 30000;

/**
 * Helper: handle an action POST/DELETE. Wraps the per-action loading flag
 * and error capture so individual buttons can show their own spinner state.
 *
 * `timeoutMs` defaults to `ACTION_TIMEOUT_MS` (30s) — see the constant
 * comment above for why. Pass an override per-call if a specific action
 * has a tighter cost profile (none today; opt-in only).
 *
 * Returns the raw response for the caller to inspect.
 */
async function performAction({
    key,
    method = 'POST',
    url,
    body,
    onError,
    onSuccess,
    timeoutMs = ACTION_TIMEOUT_MS,
}) {
    dispatch(slice.actions.setActionLoading({ key, value: true }));
    dispatch(slice.actions.setActionError({ key, error: null }));
    try {
        const response = await callAdminApi(
            url,
            method,
            body || null,
            { timeout: timeoutMs },
        );
        const ok = response.status >= 200 && response.status < 300;
        if (ok) {
            // PR #12.6 — fire transient success flash so the cockpit can
            // render a green snackbar after silent re-fetches finish.
            dispatch(slice.actions.setLastActionSuccess(key));
            if (typeof onSuccess === 'function') onSuccess(response);
        } else if (typeof onError === 'function') {
            onError(response);
        } else {
            const detail = extractDetail(
                response.data,
                `Action failed (HTTP ${response.status})`,
            );
            dispatch(slice.actions.setActionError({ key, error: detail }));
        }
        return response;
    } catch (error) {
        const message = error?.message || String(error);
        dispatch(slice.actions.setActionError({ key, error: message }));
        return { ok: false, status: 0, data: null, error };
    } finally {
        dispatch(slice.actions.setActionLoading({ key, value: false }));
    }
}

/**
 * startMigration — POST /practices/{id}/start-migration/
 * Creates `PracticeMigrationState`, takes initial pre-action checkpoint,
 * advances phase to `pre_migration`. After success, refreshes practice detail.
 *
 * BE may return 409 PRACTICE_ARCHIVED, 423 MIGRATION_BUSY, or 503
 * CHECKPOINT_STORAGE_UNAVAILABLE — all are surfaced via actionErrors.startMigration.
 */
export function startMigration(practiceId) {
    return async () => {
        if (!practiceId) return null;
        const response = await performAction({
            key: 'startMigration',
            url: `${BASE}/practices/${practiceId}/start-migration/`,
            body: {},
            onError: (resp) => {
                // PR #2 FE — surface the BE pre-flight gate in a dedicated
                // top-of-page Alert and skip the per-action error toast (the
                // gate banner already explains what's red and how to fix it).
                if (_maybeCapturePreFlightBlock(resp, 'startMigration')) return;
                const detail = extractDetail(resp.data, `Failed to start migration (HTTP ${resp.status})`);
                if (resp.status === 409) {
                    dispatch(slice.actions.setActionError({
                        key: 'startMigration',
                        error: `Cannot start: practice is archived. ${detail}`,
                    }));
                } else if (resp.status === 423) {
                    dispatch(slice.actions.setActionError({
                        key: 'startMigration',
                        error: `Another migration operation is in flight: ${detail}`,
                    }));
                } else if (resp.status === 503) {
                    dispatch(slice.actions.setStorageOutage({
                        code: 'CHECKPOINT_STORAGE_UNAVAILABLE',
                        message: detail,
                        action: 'startMigration',
                    }));
                    _clearGlobalErrorToast();
                    dispatch(slice.actions.setActionError({
                        key: 'startMigration',
                        error: `Checkpoint storage unreachable: ${detail}`,
                    }));
                } else {
                    dispatch(slice.actions.setActionError({ key: 'startMigration', error: detail }));
                }
            },
            onSuccess: () => {
                // PR #16 — clear stale storage-outage banner from a prior
                // failed attempt so the operator doesn't keep seeing red
                // after a successful retry. Same pattern used by setPhase.
                dispatch(slice.actions.clearStorageOutage());
                dispatch(fetchPracticeDetail(practiceId));
                dispatch(fetchCheckpoints(practiceId));
                // start-migration creates the PracticeMigrationState row
                // and transitions phase from "no row" → pre_migration. The
                // FeatureFlagsPanel was mounted before the row existed
                // (current_phase='none'), and without this refetch its
                // cached `current_phase` stays 'none' even as the operator
                // continues to advance through later phases — see the long
                // note on setPhase onSuccess.
                dispatch(fetchFeatureFlagsState(practiceId));
            },
        });
        return response;
    };
}

/**
 * runExecute — POST /practices/{id}/execute/
 * Body: {limit?, force_resume?}. Returns the run_id; sets activeExecuteRun
 * and starts polling.
 *
 * Requires a recent successful dry-run; BE returns 400 with code
 * `STALE_DRY_RUN` if not satisfied.
 */
export function runExecute(practiceId, opts = {}) {
    return async () => {
        if (!practiceId) return null;
        const body = {};
        if (opts.limit != null) body.limit = opts.limit;
        if (opts.forceResume) body.force_resume = true;
        const response = await performAction({
            key: 'execute',
            url: `${BASE}/practices/${practiceId}/execute/`,
            body,
            onError: (resp) => {
                // PR #2 FE — pre-flight gate handled at the top-level Alert.
                if (_maybeCapturePreFlightBlock(resp, 'execute')) return;
                // PR #3 FE — smoke gate handled at the top-level Alert.
                if (_maybeCaptureSmokeBlock(resp)) return;
                const detail = extractDetail(resp.data, `Failed to start execute (HTTP ${resp.status})`);
                dispatch(slice.actions.setActionError({ key: 'execute', error: detail }));
            },
            onSuccess: (resp) => {
                const runId = resp.data?.run_id || resp.data?.id;
                if (runId) {
                    dispatch(slice.actions.setActiveExecuteRun({ practiceId, runId }));
                    dispatch(slice.actions.setActiveRun({
                        id: runId,
                        status: resp.data?.status || 'pending',
                        progress: { done: 0, total: 0, percent: 0, eta_seconds: null },
                        last_log_lines: [],
                        warnings: [],
                    }));
                    dispatch(startRunPolling(runId, practiceId));
                }
            },
        });
        return response;
    };
}

/**
 * setPhase — POST /practices/{id}/set-phase/
 * Body: {phase, force_no_checkpoint?, confirm?}.
 * On HTTP 503 CHECKPOINT_STORAGE_UNAVAILABLE, surfaces a slice-level
 * storageOutage banner with a "Try again" hook.
 */
export function setPhase(practiceId, phase, opts = {}) {
    return async () => {
        if (!practiceId || !phase) return null;
        const body = { phase };
        if (opts.forceNoCheckpoint) body.force_no_checkpoint = true;
        if (opts.confirm) body.confirm = opts.confirm;
        const response = await performAction({
            key: 'setPhase',
            url: `${BASE}/practices/${practiceId}/set-phase/`,
            body,
            onError: (resp) => {
                // PR #2 FE — pre-flight gate handled at the top-level Alert.
                if (_maybeCapturePreFlightBlock(resp, 'setPhase')) return;
                // PR #7 FE — soak-time gate handled at the top-level Alert.
                if (_maybeCaptureSoakTimeBlock(resp)) return;
                const detail = extractDetail(resp.data, `Failed to set phase (HTTP ${resp.status})`);
                const code = resp.data?.code;
                if (resp.status === 503) {
                    dispatch(slice.actions.setStorageOutage({
                        code: 'CHECKPOINT_STORAGE_UNAVAILABLE',
                        message: detail,
                        action: 'setPhase',
                        targetPhase: phase,
                    }));
                    _clearGlobalErrorToast();
                    dispatch(slice.actions.setActionError({
                        key: 'setPhase',
                        error: `Checkpoint storage unreachable — phase change aborted. ${detail}`,
                    }));
                } else if (resp.status === 423) {
                    dispatch(slice.actions.setActionError({
                        key: 'setPhase',
                        error: `Another migration operation is in flight: ${detail}`,
                    }));
                // 2026-05-11 — surface MIGRATION_NOT_STARTED and
                // INVALID_PHASE explicitly so the operator gets an
                // actionable message instead of a generic toast that
                // they then miss.
                } else if (resp.status === 412 && code === 'MIGRATION_NOT_STARTED') {
                    dispatch(slice.actions.setActionError({
                        key: 'setPhase',
                        error: (
                            'Migration has not been started for this practice. '
                            + 'Click Start Migration before attempting to advance.'
                        ),
                    }));
                } else if (resp.status === 400 && code === 'INVALID_PHASE') {
                    dispatch(slice.actions.setActionError({
                        key: 'setPhase',
                        error: `Invalid target phase: ${detail}`,
                    }));
                } else if (resp.status === 409 && code === 'PRACTICE_ARCHIVED') {
                    dispatch(slice.actions.setActionError({
                        key: 'setPhase',
                        error: 'Cannot change phase: practice is archived.',
                    }));
                } else {
                    dispatch(slice.actions.setActionError({ key: 'setPhase', error: detail }));
                }
            },
            onSuccess: () => {
                dispatch(slice.actions.clearStorageOutage());
                dispatch(slice.actions.clearLastSoakTimeBlock());
                dispatch(fetchPracticeDetail(practiceId));
                dispatch(fetchCheckpoints(practiceId));
                // Phase changed — re-evaluate the next forward gate.
                dispatch(fetchPhaseAdvanceEligibility(practiceId));
                // Phase changed — the FeatureFlagsPanel's cached
                // `current_phase` / `allowed_combinations` would otherwise
                // stay frozen at the value seen on initial mount, leaving
                // the panel validating against the old phase's matrix
                // (`Phase: none` chip persists, V2 toggle stays blocked
                // even at `dual_canonical`). Refetch in lockstep with the
                // other cockpit panels so the legal-combo chip strip and
                // toggle gating reflect the new phase.
                dispatch(fetchFeatureFlagsState(practiceId));
            },
        });
        return response;
    };
}

/**
 * rollbackPractice — POST /practices/{id}/rollback/
 *
 * Body: ``{confirm: '<practice_name>', delete_v2_rows?: bool,
 *           accept_data_loss?: bool}``.
 *
 * PR8.5 of v4 plan / PLAN.md §3.11 -- the rollback handler returns
 * HTTP 409 ``ROLLBACK_DATA_LOSS_PENDING_CONFIRMATION`` if any
 * V2-only user activity exists since the last execute. We capture
 * the structured 409 into ``state.lastRollbackDataLossBlock`` so
 * the FE dialog can render the activity summary + acknowledgment
 * checkbox; the operator then re-POSTs with
 * ``acceptDataLoss=true``.
 */
export function rollbackPractice(
    practiceId,
    { confirm, deleteV2Rows, acceptDataLoss } = {},
) {
    return async () => {
        if (!practiceId || !confirm) return null;
        const body = { confirm };
        if (deleteV2Rows != null) body.delete_v2_rows = !!deleteV2Rows;
        if (acceptDataLoss) body.accept_data_loss = true;

        // Clear any stale block from a previous attempt.
        dispatch(slice.actions.clearLastRollbackDataLossBlock());

        const response = await performAction({
            key: 'rollback',
            url: `${BASE}/practices/${practiceId}/rollback/`,
            body,
            onError: (resp) => {
                // PR8.5 -- 409 with structured payload means activity
                // was detected. Capture it so the dialog can render.
                if (
                    resp
                    && resp.status === 409
                    && resp.data?.code === 'ROLLBACK_DATA_LOSS_PENDING_CONFIRMATION'
                ) {
                    dispatch(slice.actions.setLastRollbackDataLossBlock({
                        activitySummary: resp.data.activity_summary || null,
                        since: resp.data.since || null,
                        detail: resp.data.detail || null,
                        remediation: resp.data.remediation || null,
                    }));
                    return;  // Don't surface as a generic action error.
                }
                // Fall through to the default error toast for other
                // failure modes.
                const detail = extractDetail(
                    resp?.data,
                    `Rollback failed (HTTP ${resp?.status ?? '?'})`,
                );
                dispatch(slice.actions.setActionError({
                    key: 'rollback',
                    error: detail,
                }));
            },
            onSuccess: () => {
                dispatch(slice.actions.clearLastRollbackDataLossBlock());
                dispatch(fetchPracticeDetail(practiceId));
                dispatch(fetchCheckpoints(practiceId));
                dispatch(fetchPracticeRuns(practiceId));
                // Rollback moves the practice back to an earlier phase, so
                // the FeatureFlagsPanel's cached `current_phase` /
                // `allowed_combinations` are stale. Refetch so the legal-
                // combo chip strip reflects the new (post-rollback) phase
                // and so an operator that rolled back from `v2_canonical`
                // doesn't keep seeing V2-only as the only legal toggle.
                dispatch(fetchFeatureFlagsState(practiceId));
            },
        });
        return response;
    };
}

/**
 * resetMigration — POST /practices/{id}/reset/
 *
 * Body: { confirm: <practice_name>, wipe_ledger: bool }
 *   - wipe_ledger=false (soft): wipes migration metadata only.
 *   - wipe_ledger=true  (hard): also deletes V2 Tasks + ReminderTaskMap.
 *
 * Use when the cockpit is in an unrecoverable state and you want to start
 * over. The BE refuses if a non-terminal run is in flight (must cancel
 * first) or if hard-reset is attempted on an unsafe phase.
 */
export function resetMigration(practiceId, { confirm, wipeLedger = false } = {}) {
    return async () => {
        if (!practiceId || !confirm) return null;
        const response = await performAction({
            key: 'resetMigration',
            url: `${BASE}/practices/${practiceId}/reset/`,
            body: { confirm, wipe_ledger: !!wipeLedger },
            onSuccess: () => {
                // Refetch everything after a reset since cockpit state is wiped.
                dispatch(fetchPracticeDetail(practiceId));
                dispatch(fetchPracticeRuns(practiceId));
                dispatch(fetchCheckpoints(practiceId));
                dispatch(fetchUserOverrides(practiceId));
                dispatch(fetchStuckOutbox(practiceId));
                dispatch(fetchPhaseTransitions(practiceId));
                dispatch(fetchParityRuns(practiceId));
                dispatch(fetchDiagnostics(practiceId, { fresh: true }));
                // Reset wipes the PracticeMigrationState row — phase goes
                // back to 'none'. Refetch in line with the rest of the
                // cockpit so the FeatureFlagsPanel's cached matrix snaps
                // back to the pre-migration "V1 on / V2 off" only.
                dispatch(fetchFeatureFlagsState(practiceId));
            },
        });
        return response;
    };
}

/**
 * releaseLock — POST /practices/{id}/release-lock/
 *
 * Force-release the practice-scoped tasks-migration lock. Used to recover
 * from an orphaned lock (gunicorn worker killed mid-request before its
 * finally-block ran). BE requires `confirm` to match the practice name
 * exactly, mirroring the break-glass shape used by reset / force_no_soak.
 *
 * Body: { confirm: <practice_name> }
 *
 * On success the diagnostics cache is busted server-side, so the next
 * fetchDiagnostics call reflects the cleared lock immediately.
 */
export function releaseLock(practiceId, { confirm } = {}) {
    return async () => {
        if (!practiceId || !confirm) return null;
        const response = await performAction({
            key: 'releaseLock',
            url: `${BASE}/practices/${practiceId}/release-lock/`,
            body: { confirm },
            onSuccess: () => {
                // Refresh diagnostics so the cockpit chip flips off
                // and the "orphaned lock" warning clears.
                dispatch(fetchDiagnostics(practiceId, { fresh: true }));
            },
        });
        return response;
    };
}

/**
 * fetchPreFlightHealth — GET /practices/{id}/pre-flight-health/
 *
 * PR #2 FE. Snapshot of 8 infrastructure probes (Celery workers, beat,
 * Redis broker, checkpoint storage, mirror signals, outbox queue, registered
 * tasks). When `overall_status === 'fail'` the cockpit hard-blocks the four
 * primary action buttons (start-migration, dry-run, execute, set-phase);
 * the BE enforces the same gate by returning 412 PRE_FLIGHT_FAILED.
 *
 * 15s axios timeout: the BE runs `run_pre_flight_probes` which has a 5s
 * wall-clock budget; two of the probes (`celery_workers_active`,
 * `tasks_migration_tasks_registered`) do Celery `inspect()` broadcasts
 * over the broker with their own 2s timeouts. The default 10s axios
 * window was killing this call on staging with HTTP 0 before the BE
 * finished. 2026-05-11 added a 5s TTL cache on the BE side (see
 * `apps/tasks_migration/pre_flight.py:cached_pre_flight_probes`), so
 * the warm path is now sub-millisecond; the timeout bump is headroom
 * for the cold path against a degraded broker.
 *
 * No `fresh` param — the BE already serves uncached snapshots.
 */
export function fetchPreFlightHealth(practiceId) {
    return async () => {
        if (!practiceId) return null;
        try {
            dispatch(slice.actions.setPanelLoading({ key: 'preFlightHealth', value: true }));
            dispatch(slice.actions.setPanelError({ key: 'preFlightHealth', error: null }));
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/pre-flight-health/`,
                'GET',
                null,
                { timeout: 15000 },
            );
            if (response.status === 200) {
                dispatch(slice.actions.setPreFlightHealth(response.data));
            } else if (response.status === 0) {
                dispatch(slice.actions.setPanelError({
                    key: 'preFlightHealth',
                    error: 'Network timeout fetching pre-flight health.',
                }));
            } else {
                const detail = extractDetail(
                    response.data,
                    `Failed to fetch pre-flight health (HTTP ${response.status})`,
                );
                dispatch(slice.actions.setPanelError({ key: 'preFlightHealth', error: detail }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setPanelError({ key: 'preFlightHealth', error: message }));
            return { ok: false, status: 0, error };
        } finally {
            dispatch(slice.actions.setPanelLoading({ key: 'preFlightHealth', value: false }));
        }
    };
}

/**
 * fetchPhaseAdvanceEligibility — GET /practices/{id}/phase-advance-eligibility/
 *
 * PR #7 FE. Returns the soak-time progress for the next forward phase
 * advance. Drives per-option text + disabled state on the Advance Phase
 * menu. The cockpit refreshes this on mount and after parity / phase
 * events. Server-side `set-phase` enforces the same gate via 412
 * SOAK_TIME_NOT_MET.
 */
export function fetchPhaseAdvanceEligibility(practiceId) {
    return async () => {
        if (!practiceId) return null;
        try {
            dispatch(slice.actions.setPanelLoading({ key: 'phaseAdvanceEligibility', value: true }));
            dispatch(slice.actions.setPanelError({ key: 'phaseAdvanceEligibility', error: null }));
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/phase-advance-eligibility/`,
            );
            if (response.status === 200) {
                dispatch(slice.actions.setPhaseAdvanceEligibility(response.data));
            } else if (response.status === 0) {
                dispatch(slice.actions.setPanelError({
                    key: 'phaseAdvanceEligibility',
                    error: 'Network timeout fetching phase advance eligibility.',
                }));
            } else {
                const detail = extractDetail(
                    response.data,
                    `Failed to fetch phase advance eligibility (HTTP ${response.status})`,
                );
                dispatch(slice.actions.setPanelError({ key: 'phaseAdvanceEligibility', error: detail }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setPanelError({ key: 'phaseAdvanceEligibility', error: message }));
            return { ok: false, status: 0, error };
        } finally {
            dispatch(slice.actions.setPanelLoading({ key: 'phaseAdvanceEligibility', value: false }));
        }
    };
}

/**
 * fetchDiagnostics — GET /practices/{id}/diagnostics/
 *
 * Snapshot of Celery / broker / practice / data health used by the
 * DiagnosticsPanel. Pass `fresh=true` to bypass the BE 10s cache after
 * an action that should immediately reflect new state.
 */
export function fetchDiagnostics(practiceId, { fresh = false } = {}) {
    return async () => {
        if (!practiceId) return null;
        try {
            dispatch(slice.actions.setPanelLoading({ key: 'diagnostics', value: true }));
            const url = `${BASE}/practices/${practiceId}/diagnostics/${fresh ? '?fresh=1' : ''}`;
            // 30s timeout: the BE diagnostics view does a Celery
            // broadcast inspect (per-worker timeout=2s, no global cap) +
            // 6 COUNT(*) queries on cold cache. The default 10s wraps
            // around past axios's ECONNABORTED, which surfaces here as
            // status=0 even though the response eventually arrives. 30s
            // is comfortable headroom; the BE caches for 10s after the
            // first call so subsequent fetches are sub-second.
            const response = await callAdminApi(url, 'GET', null, { timeout: 30000 });
            if (response.status === 200) {
                dispatch(slice.actions.setDiagnostics(response.data));
            } else if (response.status === 0) {
                dispatch(slice.actions.setPanelError({
                    key: 'diagnostics',
                    error: 'Network timeout — the diagnostics endpoint took too long to respond. The Celery broadcast or DB counts are slow; retrying in 30s.',
                }));
            } else {
                const detail = extractDetail(response.data, `Failed to fetch diagnostics (HTTP ${response.status})`);
                dispatch(slice.actions.setPanelError({ key: 'diagnostics', error: detail }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setPanelError({ key: 'diagnostics', error: message }));
            return { ok: false, status: 0, error };
        } finally {
            dispatch(slice.actions.setPanelLoading({ key: 'diagnostics', value: false }));
        }
    };
}

/**
 * setMirrorDirection — POST /practices/{id}/set-mirror-direction/
 * Body: {canonical_side: 'v1'|'v2'}. Phase-guarded (only valid during
 * `dual_canonical`); BE will reject with 409 otherwise.
 */
export function setMirrorDirection(practiceId, canonicalSide) {
    return async () => {
        if (!practiceId || !canonicalSide) return null;
        const response = await performAction({
            key: 'setMirrorDirection',
            url: `${BASE}/practices/${practiceId}/set-mirror-direction/`,
            body: { canonical_side: canonicalSide },
            onSuccess: () => {
                dispatch(fetchPracticeDetail(practiceId));
                dispatch(fetchCheckpoints(practiceId));
            },
        });
        return response;
    };
}

/**
 * setV2Flag — POST /practices/{id}/v2-flag/
 * Body: {enabled: bool, confirm: '<practice name>'}.
 *
 * Phase interlock (BE-enforced): to ENABLE, phase must be 'dual_canonical'
 * or 'v2_canonical'. The BE returns HTTP 412 V2_FLAG_REQUIRES_BACK_MIRROR_ACTIVE
 * otherwise — surfaced as a friendly error.
 *
 * Disabling is always allowed (rollback safety).
 */
export function setV2Flag(practiceId, enabled, confirm) {
    return async () => {
        if (!practiceId || typeof enabled !== 'boolean' || !confirm) return null;
        const response = await performAction({
            key: 'setV2Flag',
            url: `${BASE}/practices/${practiceId}/v2-flag/`,
            body: { enabled, confirm },
            onError: (resp) => {
                const detail = extractDetail(
                    resp.data,
                    `Failed to ${enabled ? 'enable' : 'disable'} the new system (HTTP ${resp.status})`,
                );
                if (resp.status === 412 && resp.data?.code === 'V2_FLAG_REQUIRES_BACK_MIRROR_ACTIVE') {
                    dispatch(slice.actions.setActionError({
                        key: 'setV2Flag',
                        error: 'Advance the phase first. The new system can only be enabled while back-mirror is active (phase "Both systems live" or "New system live").',
                    }));
                } else if (resp.status === 400 && resp.data?.code === 'V2_FLAG_CONFIRM_MISMATCH') {
                    dispatch(slice.actions.setActionError({
                        key: 'setV2Flag',
                        error: 'Confirmation must match the practice name exactly.',
                    }));
                } else if (resp.status === 503) {
                    dispatch(slice.actions.setStorageOutage({
                        code: 'CHECKPOINT_STORAGE_UNAVAILABLE',
                        message: detail,
                        action: 'setV2Flag',
                    }));
                    _clearGlobalErrorToast();
                    dispatch(slice.actions.setActionError({
                        key: 'setV2Flag',
                        error: `Checkpoint storage unreachable: ${detail}`,
                    }));
                } else {
                    dispatch(slice.actions.setActionError({ key: 'setV2Flag', error: detail }));
                }
            },
            onSuccess: () => {
                dispatch(fetchPracticeDetail(practiceId));
                dispatch(fetchCheckpoints(practiceId));
            },
        });
        return response;
    };
}

/**
 * takeManualSnapshot — POST /practices/{id}/checkpoints/
 * Body: {label?, kind: 'manual_snapshot'}. Returns {key, size_bytes}.
 * On success, refreshes the checkpoint list and surfaces lastSnapshotKey.
 */
export function takeManualSnapshot(practiceId, label) {
    return async () => {
        if (!practiceId) return null;
        const body = { kind: 'manual_snapshot' };
        if (label) body.label = label;
        const response = await performAction({
            key: 'snapshot',
            url: `${BASE}/practices/${practiceId}/checkpoints/`,
            body,
            onSuccess: (resp) => {
                // PR #16 — clear stale storage-outage banner from a prior
                // failed attempt. Manual snapshot success proves storage
                // is available again; the operator should see green now.
                dispatch(slice.actions.clearStorageOutage());
                const key = resp.data?.key || null;
                dispatch(slice.actions.setLastSnapshotKey(key));
                dispatch(fetchCheckpoints(practiceId));
            },
        });
        return response;
    };
}

/**
 * pauseMirror — POST /practices/{id}/pause-mirror/
 * Body: {reason, auto_resume_minutes?}. Endpoint may not yet exist on BE
 * (lands in PR #11). On 404, surfaces a "Coming in PR #11" message instead
 * of treating it as a hard failure.
 */
export function pauseMirror(practiceId, { reason, autoResumeMinutes } = {}) {
    return async () => {
        if (!practiceId || !reason) return null;
        const body = { reason };
        if (autoResumeMinutes != null) body.auto_resume_minutes = autoResumeMinutes;
        const response = await performAction({
            key: 'pauseMirror',
            url: `${BASE}/practices/${practiceId}/pause-mirror/`,
            body,
            onError: (resp) => {
                if (resp.status === 404) {
                    dispatch(slice.actions.setActionError({
                        key: 'pauseMirror',
                        error: 'pause-mirror endpoint not available — implementation in PR #11.',
                    }));
                } else {
                    const detail = extractDetail(resp.data, `Failed to pause mirror (HTTP ${resp.status})`);
                    dispatch(slice.actions.setActionError({ key: 'pauseMirror', error: detail }));
                }
            },
            onSuccess: () => {
                dispatch(fetchPracticeDetail(practiceId));
            },
        });
        return response;
    };
}

/**
 * resumeMirror — POST /practices/{id}/resume-mirror/
 * Body: {}. Endpoint may not yet exist on BE; 404 surfaces "Coming in PR #11".
 */
export function resumeMirror(practiceId) {
    return async () => {
        if (!practiceId) return null;
        const response = await performAction({
            key: 'resumeMirror',
            url: `${BASE}/practices/${practiceId}/resume-mirror/`,
            body: {},
            onError: (resp) => {
                if (resp.status === 404) {
                    dispatch(slice.actions.setActionError({
                        key: 'resumeMirror',
                        error: 'resume-mirror endpoint not available — implementation in PR #11.',
                    }));
                } else {
                    const detail = extractDetail(resp.data, `Failed to resume mirror (HTTP ${resp.status})`);
                    dispatch(slice.actions.setActionError({ key: 'resumeMirror', error: detail }));
                }
            },
            onSuccess: () => {
                dispatch(fetchPracticeDetail(practiceId));
            },
        });
        return response;
    };
}

/**
 * pauseRun / resumeRun / cancelRun — POST /runs/{id}/{pause|resume|cancel}/
 * Used by Step 4 Execute progress card. Endpoints may not exist yet on BE
 * (PR #11 territory) — 404 is treated as soft skip.
 */
export function pauseRun(runId) {
    return async () => {
        if (!runId) return null;
        const response = await performAction({
            key: 'pauseRun',
            url: `${BASE}/runs/${runId}/pause/`,
            body: {},
            onError: (resp) => {
                if (resp.status === 404) {
                    dispatch(slice.actions.setActionError({
                        key: 'pauseRun',
                        error: 'pause-run endpoint not available — PR #11.',
                    }));
                } else {
                    const detail = extractDetail(resp.data, `Failed to pause run (HTTP ${resp.status})`);
                    dispatch(slice.actions.setActionError({ key: 'pauseRun', error: detail }));
                }
            },
            onSuccess: () => {
                dispatch(pollRun(runId));
            },
        });
        return response;
    };
}

export function resumeRun(runId) {
    return async () => {
        if (!runId) return null;
        const response = await performAction({
            key: 'resumeRun',
            url: `${BASE}/runs/${runId}/resume/`,
            body: {},
            onError: (resp) => {
                if (resp.status === 404) {
                    dispatch(slice.actions.setActionError({
                        key: 'resumeRun',
                        error: 'resume-run endpoint not available — PR #11.',
                    }));
                } else {
                    const detail = extractDetail(resp.data, `Failed to resume run (HTTP ${resp.status})`);
                    dispatch(slice.actions.setActionError({ key: 'resumeRun', error: detail }));
                }
            },
            onSuccess: (resp) => {
                // BE includes `worker_resumable: false` + `next_steps` so the
                // operator knows resume only flips the cooperative-pause
                // flag — the Celery worker isn't auto-respawned. Surface the
                // hint in actionErrors so the cockpit can show a yellow
                // banner alongside the success snackbar.
                const data = resp?.data || {};
                if (data.worker_resumable === false && data.next_steps) {
                    dispatch(slice.actions.setActionError({
                        key: 'resumeRun',
                        error: data.next_steps,
                    }));
                }
                dispatch(pollRun(runId));
                dispatch(startRunPolling(runId));
            },
        });
        return response;
    };
}

export function cancelRun(runId) {
    return async () => {
        if (!runId) return null;
        const response = await performAction({
            key: 'cancelRun',
            url: `${BASE}/runs/${runId}/cancel/`,
            body: {},
            onError: (resp) => {
                if (resp.status === 404) {
                    dispatch(slice.actions.setActionError({
                        key: 'cancelRun',
                        error: 'cancel-run endpoint not available — PR #11.',
                    }));
                } else {
                    const detail = extractDetail(resp.data, `Failed to cancel run (HTTP ${resp.status})`);
                    dispatch(slice.actions.setActionError({ key: 'cancelRun', error: detail }));
                }
            },
            onSuccess: () => {
                dispatch(pollRun(runId));
            },
        });
        return response;
    };
}

/**
 * runParityCheck — POST /practices/{id}/parity-check/
 * BE shipped in PR #11. Returns the parity_run_id when available; we then
 * start the parity-run polling loop.
 *
 * NOTE on the row lifecycle: the BE endpoint creates a *placeholder*
 * ``ParityCheckRun`` row (apps/management/tasks_migration_views.py:5698)
 * with ``finished_at=NULL`` and dispatches a Celery worker. The worker
 * (apps/tasks_migration/parity.py:819) then creates a SEPARATE completed
 * row — it does not update the placeholder. That means polling the
 * placeholder's id will never see a terminal state (the placeholder stays
 * unfinished forever). The cockpit only learns about the worker's row
 * via the cockpit's independent 30s observability poll, which is also why
 * the Recommended Next Step banner can stay stale for up to 30s after
 * Run Now. The fallback refresh below covers that gap until the BE row
 * lifecycle is unified.
 */
const PARITY_FALLBACK_REFRESH_MS = 3000;

export function runParityCheck(practiceId) {
    return async () => {
        if (!practiceId) return null;
        const response = await performAction({
            key: 'parityCheck',
            url: `${BASE}/practices/${practiceId}/parity-check/`,
            body: {},
            onError: (resp) => {
                if (resp.status === 404) {
                    dispatch(slice.actions.setActionError({
                        key: 'parityCheck',
                        error: 'Parity-check endpoint not available on this BE.',
                    }));
                } else {
                    const detail = extractDetail(resp.data, `Failed to start parity check (HTTP ${resp.status})`);
                    dispatch(slice.actions.setActionError({ key: 'parityCheck', error: detail }));
                }
            },
            onSuccess: (resp) => {
                const runId = resp.data?.parity_run_id
                    || resp.data?.run_id
                    || resp.data?.id;
                if (runId) {
                    // Seed the snapshot so UI flips to "running" instantly.
                    dispatch(slice.actions.setActiveParityRun({
                        id: runId,
                        status: resp.data?.status || 'pending',
                        drift_count: null,
                        drift_breakdown: null,
                    }));
                    dispatch(startParityRunPolling(practiceId, runId));
                }
                // Fallback refresh: the polling loop above polls the
                // placeholder row, which the worker never finishes (see
                // module-level note). Without this, the parityRuns list
                // and Recommended Next Step banner only refresh on the
                // cockpit's independent 30s observability poll — meaning
                // the banner can show "Latest data match found N
                // mismatch(es)" for ~30s after a Run Now that resolved
                // them. A typical parity run completes in ~2s, so a
                // single delayed refresh covers the vast majority of
                // cases. Longer runs still get caught by the 30s poll.
                setTimeout(() => {
                    dispatch(fetchParityRuns(practiceId));
                    dispatch(fetchRecommendedNextStep(practiceId));
                    dispatch(fetchMigrationSafety(practiceId));
                }, PARITY_FALLBACK_REFRESH_MS);
            },
        });
        return response;
    };
}

// --------------- PR #12 thunks: observability panel data ---------------

// Module-level handle for the parity-run polling loop (single in-flight at a time)
let _parityPollTimer = null;
let _parityPollRunId = null;
const PARITY_TERMINAL_STATUSES = new Set(['succeeded', 'failed', 'cancelled']);

async function fetchPanel({ key, url, setter, transform }) {
    dispatch(slice.actions.setPanelLoading({ key, value: true }));
    dispatch(slice.actions.setPanelError({ key, error: null }));
    try {
        const response = await callAdminApi(url);
        if (response.status === 200) {
            const list = unwrapList(response.data);
            const out = typeof transform === 'function' ? transform(list, response.data) : list;
            dispatch(setter(out));
        } else if (response.status === 404) {
            // Endpoint not yet deployed — keep panel empty, surface a soft note.
            dispatch(setter([]));
            dispatch(slice.actions.setPanelError({
                key,
                error: 'Endpoint not available on this BE.',
            }));
        } else {
            dispatch(setter([]));
            const detail = extractDetail(response.data, `Failed to load (HTTP ${response.status})`);
            dispatch(slice.actions.setPanelError({ key, error: detail }));
        }
    } catch (error) {
        dispatch(setter([]));
        dispatch(slice.actions.setPanelError({ key, error: error?.message || String(error) }));
    } finally {
        dispatch(slice.actions.setPanelLoading({ key, value: false }));
    }
}

/**
 * fetchStuckOutbox — GET /practices/{id}/outbox/?...
 *
 * Default filter (no `filter` arg, or `'in_flight_60'`) keeps the legacy
 * behaviour: IN_FLIGHT rows older than 60s. Other filters are surfaced as
 * UI toggles in StuckOutboxPanel:
 *
 *   - 'pending_30' → state=PENDING&older_than_seconds=30
 *   - 'failed'     → state=FAILED
 *
 * If the BE doesn't support the requested filter combination, the panel
 * still renders the response — server-side filtering is preferred but the
 * UI degrades gracefully if older BEs ignore the params.
 */
export function fetchStuckOutbox(practiceId, filter = 'in_flight_60') {
    return async () => {
        if (!practiceId) return;
        let qs;
        switch (filter) {
            case 'pending_30':
                qs = 'state=PENDING&older_than_seconds=30';
                break;
            case 'failed':
                qs = 'state=FAILED';
                break;
            case 'in_flight_60':
            default:
                qs = 'state=in_flight&older_than_seconds=60';
                break;
        }
        await fetchPanel({
            key: 'stuckOutbox',
            url: `${BASE}/practices/${practiceId}/outbox/?${qs}`,
            setter: slice.actions.setStuckOutbox,
        });
    };
}

/**
 * fetchV2DeleteAttempts — GET /practices/{id}/v2-delete-attempts/
 */
export function fetchV2DeleteAttempts(practiceId) {
    return async () => {
        if (!practiceId) return;
        await fetchPanel({
            key: 'v2DeleteAttempts',
            url: `${BASE}/practices/${practiceId}/v2-delete-attempts/`,
            setter: slice.actions.setV2DeleteAttempts,
        });
    };
}

/**
 * fetchPhaseTransitions — GET /practices/{id}/phase-transitions/
 */
export function fetchPhaseTransitions(practiceId) {
    return async () => {
        if (!practiceId) return;
        await fetchPanel({
            key: 'phaseTransitions',
            url: `${BASE}/practices/${practiceId}/phase-transitions/`,
            setter: slice.actions.setPhaseTransitionsList,
        });
    };
}

/**
 * fetchConflictHistory — GET /practices/{id}/conflict-history/
 */
export function fetchConflictHistory(practiceId) {
    return async () => {
        if (!practiceId) return;
        await fetchPanel({
            key: 'conflictHistory',
            url: `${BASE}/practices/${practiceId}/conflict-history/`,
            setter: slice.actions.setConflictHistory,
        });
    };
}

/**
 * fetchParityRuns — GET /practices/{id}/parity-runs/
 * Sorts to keep the most recent first.
 */
export function fetchParityRuns(practiceId) {
    return async () => {
        if (!practiceId) return;
        await fetchPanel({
            key: 'parityRuns',
            url: `${BASE}/practices/${practiceId}/parity-runs/`,
            setter: slice.actions.setParityRuns,
            transform: (list) => {
                const sorted = [...list].sort((a, b) => {
                    const ta = a.finished_at || a.started_at || a.created_at;
                    const tb = b.finished_at || b.started_at || b.created_at;
                    const da = ta ? Date.parse(ta) : 0;
                    const db = tb ? Date.parse(tb) : 0;
                    return db - da;
                });
                return sorted;
            },
        });
    };
}

/**
 * pollParityRun — GET /parity-runs/{id}/ once. Updates activeParityRun.
 * Returns the run snapshot (or null on failure).
 */
export function pollParityRun(parityRunId) {
    return async () => {
        if (!parityRunId) return null;
        try {
            const response = await callAdminApi(`${BASE}/parity-runs/${parityRunId}/`);
            if (response.status === 200 && response.data) {
                dispatch(slice.actions.setActiveParityRun(response.data));
                return response.data;
            }
            return null;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.warn('[tasksV2Migration.pollParityRun] poll error', error);
            return null;
        }
    };
}

/**
 * startParityRunPolling — same shape as startRunPolling but for ParityCheckRun.
 * Refreshes the parity-runs list on terminal status so the panel updates.
 */
export function startParityRunPolling(practiceId, parityRunId) {
    return () => {
        if (!parityRunId) return;
        if (_parityPollTimer && _parityPollRunId !== parityRunId) {
            clearInterval(_parityPollTimer);
            _parityPollTimer = null;
        }
        if (_parityPollTimer) return;
        _parityPollRunId = parityRunId;

        const tick = async () => {
            if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
                return;
            }
            const run = await dispatch(pollParityRun(parityRunId));
            if (run && run.status && PARITY_TERMINAL_STATUSES.has(run.status)) {
                clearInterval(_parityPollTimer);
                _parityPollTimer = null;
                _parityPollRunId = null;
                if (practiceId) {
                    dispatch(fetchParityRuns(practiceId));
                    dispatch(fetchPracticeDetail(practiceId));
                    // Soak-time eligibility uses the consecutive-zero-drift
                    // count off ParityCheckRun rows. A run finishing changes
                    // that count, so refresh the eligibility snapshot here —
                    // otherwise the Advance Phase menu sits at a stale 0/4
                    // even after the operator just completed a clean run.
                    dispatch(fetchPhaseAdvanceEligibility(practiceId));
                    // Recommended Next Step also reads from the latest parity
                    // run + outbox snapshot; refresh it for the same reason.
                    dispatch(fetchRecommendedNextStep(practiceId));
                    dispatch(fetchMigrationSafety(practiceId));
                }
            }
        };

        tick();
        _parityPollTimer = setInterval(tick, POLL_INTERVAL_MS);
    };
}

export function stopParityRunPolling() {
    return () => {
        if (_parityPollTimer) {
            clearInterval(_parityPollTimer);
            _parityPollTimer = null;
            _parityPollRunId = null;
        }
    };
}

/**
 * retryOutboxRow — POST /outbox/{id}/retry/
 * Re-enqueues the outbox row with the same idempotency key. On success we
 * remove the row from the local list (BE will surface it again on the next
 * fetch if it's still stuck — that's the desired feedback loop).
 */
export function retryOutboxRow(practiceId, outboxId) {
    return async () => {
        if (!outboxId) return null;
        dispatch(slice.actions.setOutboxActionLoading({ id: outboxId, value: true }));
        dispatch(slice.actions.setOutboxActionError({ id: outboxId, error: null }));
        try {
            const response = await callAdminApi(`${BASE}/outbox/${outboxId}/retry/`, 'POST', {});
            const ok = response.status >= 200 && response.status < 300;
            if (ok) {
                dispatch(slice.actions.removeStuckOutboxRow(outboxId));
                if (practiceId) dispatch(fetchStuckOutbox(practiceId));
            } else {
                const detail = extractDetail(
                    response.data,
                    `Retry failed (HTTP ${response.status})`,
                );
                dispatch(slice.actions.setOutboxActionError({ id: outboxId, error: detail }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setOutboxActionError({ id: outboxId, error: message }));
            return { ok: false, status: 0, data: null, error };
        } finally {
            dispatch(slice.actions.setOutboxActionLoading({ id: outboxId, value: false }));
        }
    };
}

/**
 * skipOutboxRow — POST /outbox/{id}/skip/
 * Two-step confirmation lives in the panel UI. We just fire the request.
 */
export function skipOutboxRow(practiceId, outboxId) {
    return async () => {
        if (!outboxId) return null;
        dispatch(slice.actions.setOutboxActionLoading({ id: outboxId, value: true }));
        dispatch(slice.actions.setOutboxActionError({ id: outboxId, error: null }));
        try {
            const response = await callAdminApi(`${BASE}/outbox/${outboxId}/skip/`, 'POST', {});
            const ok = response.status >= 200 && response.status < 300;
            if (ok) {
                dispatch(slice.actions.removeStuckOutboxRow(outboxId));
                if (practiceId) dispatch(fetchStuckOutbox(practiceId));
            } else {
                const detail = extractDetail(
                    response.data,
                    `Skip failed (HTTP ${response.status})`,
                );
                dispatch(slice.actions.setOutboxActionError({ id: outboxId, error: detail }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setOutboxActionError({ id: outboxId, error: message }));
            return { ok: false, status: 0, data: null, error };
        } finally {
            dispatch(slice.actions.setOutboxActionLoading({ id: outboxId, value: false }));
        }
    };
}

// --------------- PR #13 thunks: checkpoint management ---------------

/**
 * archiveCheckpoint — POST /practices/{id}/checkpoints/{cp_id}/archive/
 * Soft-archives the checkpoint (idempotent). Refreshes list on success.
 */
export function archiveCheckpoint({ practiceId, checkpointId } = {}) {
    return async () => {
        if (!practiceId || checkpointId == null) return null;
        const response = await performAction({
            key: 'archiveCheckpoint',
            url: `${BASE}/practices/${practiceId}/checkpoints/${checkpointId}/archive/`,
            body: {},
            onError: (resp) => {
                const detail = extractDetail(
                    resp.data,
                    `Failed to archive checkpoint (HTTP ${resp.status})`,
                );
                dispatch(slice.actions.setActionError({ key: 'archiveCheckpoint', error: detail }));
            },
            onSuccess: () => {
                dispatch(fetchCheckpoints(practiceId));
            },
        });
        return response;
    };
}

/**
 * restoreCheckpoint — POST /practices/{id}/checkpoints/{cp_id}/restore/
 * Undoes a previous archive. Refreshes list on success.
 */
export function restoreCheckpoint({ practiceId, checkpointId } = {}) {
    return async () => {
        if (!practiceId || checkpointId == null) return null;
        const response = await performAction({
            key: 'restoreCheckpoint',
            url: `${BASE}/practices/${practiceId}/checkpoints/${checkpointId}/restore/`,
            body: {},
            onError: (resp) => {
                const detail = extractDetail(
                    resp.data,
                    `Failed to restore checkpoint (HTTP ${resp.status})`,
                );
                dispatch(slice.actions.setActionError({ key: 'restoreCheckpoint', error: detail }));
            },
            onSuccess: () => {
                dispatch(fetchCheckpoints(practiceId));
            },
        });
        return response;
    };
}

/**
 * deleteCheckpoint — DELETE /practices/{id}/checkpoints/{cp_id}/
 * Hard-deletes the blob and row. BE refuses with 409 CHECKPOINT_NOT_ARCHIVED
 * if the row isn't archived, or 503 CHECKPOINT_STORAGE_UNAVAILABLE on storage
 * write failure — both surfaced via actionErrors.deleteCheckpoint.
 */
export function deleteCheckpoint({ practiceId, checkpointId } = {}) {
    return async () => {
        if (!practiceId || checkpointId == null) return null;
        const response = await performAction({
            key: 'deleteCheckpoint',
            method: 'DELETE',
            url: `${BASE}/practices/${practiceId}/checkpoints/${checkpointId}/`,
            onError: (resp) => {
                const detail = extractDetail(
                    resp.data,
                    `Failed to delete checkpoint (HTTP ${resp.status})`,
                );
                if (resp.status === 409) {
                    const code = resp.data?.code || resp.data?.error_code;
                    if (code === 'CHECKPOINT_NOT_ARCHIVED') {
                        dispatch(slice.actions.setActionError({
                            key: 'deleteCheckpoint',
                            error: 'Checkpoint must be archived before it can be permanently deleted.',
                        }));
                        return;
                    }
                    dispatch(slice.actions.setActionError({ key: 'deleteCheckpoint', error: detail }));
                } else if (resp.status === 503) {
                    dispatch(slice.actions.setStorageOutage({
                        code: 'CHECKPOINT_STORAGE_UNAVAILABLE',
                        message: detail,
                        action: 'deleteCheckpoint',
                    }));
                    _clearGlobalErrorToast();
                    dispatch(slice.actions.setActionError({
                        key: 'deleteCheckpoint',
                        error: `Checkpoint storage unreachable: ${detail}`,
                    }));
                } else {
                    dispatch(slice.actions.setActionError({ key: 'deleteCheckpoint', error: detail }));
                }
            },
            onSuccess: () => {
                dispatch(fetchCheckpoints(practiceId));
            },
        });
        return response;
    };
}

/**
 * fetchCheckpointDownloadUrl — GET /practices/{id}/checkpoints/{cp_id}/download-url/
 * Returns `{ url, expires_at, size_bytes }` directly to the caller. One-shot:
 * we do NOT cache globally; the caller opens a new tab with the pre-signed URL.
 */
export function fetchCheckpointDownloadUrl({ practiceId, checkpointId } = {}) {
    return async () => {
        if (!practiceId || checkpointId == null) {
            return { ok: false, status: 0, data: null };
        }
        try {
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/checkpoints/${checkpointId}/download-url/`,
            );
            return response;
        } catch (error) {
            return { ok: false, status: 0, data: null, error };
        }
    };
}

/**
 * fetchCheckpointPreview — GET /practices/{id}/checkpoints/{cp_id}/preview/
 * Returns either `{ size_bytes, content_type, row_count, first_rows: [...] }`
 * or `{ size_bytes, content_type, preview_unavailable: true, reason }`.
 * One-shot; not stored globally.
 */
export function fetchCheckpointPreview({ practiceId, checkpointId } = {}) {
    return async () => {
        if (!practiceId || checkpointId == null) {
            return { ok: false, status: 0, data: null };
        }
        try {
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/checkpoints/${checkpointId}/preview/`,
            );
            return response;
        } catch (error) {
            return { ok: false, status: 0, data: null, error };
        }
    };
}

/**
 * cleanupCheckpoints — POST /practices/{id}/checkpoints/cleanup/
 * Body: { older_than_days, strategy: 'archive'|'delete', include_unarchived_for_delete }.
 * Returns `{ archived, deleted, skipped: [{id, reason}] }` to the caller.
 * BE returns 409 if `delete` strategy used on a non-terminal phase — surfaced
 * via the per-action error.
 */
export function cleanupCheckpoints({
    practiceId, olderThanDays, strategy, includeUnarchivedForDelete,
} = {}) {
    return async () => {
        if (!practiceId || !strategy) return null;
        const body = {
            older_than_days: Number(olderThanDays) || 30,
            strategy,
        };
        if (includeUnarchivedForDelete) {
            body.include_unarchived_for_delete = true;
        }
        const response = await performAction({
            key: 'cleanupCheckpoints',
            url: `${BASE}/practices/${practiceId}/checkpoints/cleanup/`,
            body,
            onError: (resp) => {
                const detail = extractDetail(
                    resp.data,
                    `Cleanup failed (HTTP ${resp.status})`,
                );
                if (resp.status === 409) {
                    dispatch(slice.actions.setActionError({
                        key: 'cleanupCheckpoints',
                        error: `Cleanup refused: ${detail}`,
                    }));
                } else {
                    dispatch(slice.actions.setActionError({ key: 'cleanupCheckpoints', error: detail }));
                }
            },
            onSuccess: () => {
                dispatch(fetchCheckpoints(practiceId));
            },
        });
        return response;
    };
}

// --------------- PR #1 (FE) thunks: outbox drain + recover-stuck ---------------

/**
 * drainOutbox — POST /practices/{id}/outbox/drain/
 *
 * Body: { direction: 'forward'|'back', batch_size?: int (1..500),
 *         max_runtime_s?: int (1..60) }.
 * Returns 202 with { task_id, direction, batch_size, max_runtime_s }.
 *
 * Errors surfaced via actionErrors.drainOutbox:
 *   - 409 PRACTICE_ARCHIVED
 *   - 400 INVALID_DIRECTION
 *   - 503 DRAIN_DISPATCH_FAILED
 *
 * On success, refreshes the diagnostics snapshot so the operator sees the
 * task id register in the in-flight runs / outbox_summary block.
 */
export function drainOutbox({
    practiceId, direction, batchSize, maxRuntimeS,
} = {}) {
    return async () => {
        if (!practiceId || !direction) return null;
        const body = { direction };
        if (batchSize != null) body.batch_size = Number(batchSize);
        if (maxRuntimeS != null) body.max_runtime_s = Number(maxRuntimeS);
        const response = await performAction({
            key: 'drainOutbox',
            url: `${BASE}/practices/${practiceId}/outbox/drain/`,
            body,
            onError: (resp) => {
                const detail = extractDetail(
                    resp.data,
                    `Failed to dispatch drain (HTTP ${resp.status})`,
                );
                if (resp.status === 409) {
                    dispatch(slice.actions.setActionError({
                        key: 'drainOutbox',
                        error: `Cannot drain: practice is archived. ${detail}`,
                    }));
                } else if (resp.status === 400) {
                    dispatch(slice.actions.setActionError({
                        key: 'drainOutbox',
                        error: `Invalid direction. ${detail}`,
                    }));
                } else if (resp.status === 503) {
                    dispatch(slice.actions.setActionError({
                        key: 'drainOutbox',
                        error: `Drain dispatch failed — broker unreachable: ${detail}`,
                    }));
                } else {
                    dispatch(slice.actions.setActionError({ key: 'drainOutbox', error: detail }));
                }
            },
            onSuccess: () => {
                // Refresh diagnostics so the new in-flight task / outbox
                // counts appear in the panel without manual refresh.
                dispatch(fetchDiagnostics(practiceId, { fresh: true }));
            },
        });
        return response;
    };
}

/**
 * recoverStuckOutbox — POST /practices/{id}/outbox/recover-stuck/
 *
 * No body. Returns 202 with { task_id }.
 *
 * Errors surfaced via actionErrors.recoverStuckOutbox:
 *   - 409 PRACTICE_ARCHIVED
 *   - 503 RECOVER_DISPATCH_FAILED
 *
 * On success, refreshes diagnostics.
 */
export function recoverStuckOutbox({ practiceId } = {}) {
    return async () => {
        if (!practiceId) return null;
        const response = await performAction({
            key: 'recoverStuckOutbox',
            url: `${BASE}/practices/${practiceId}/outbox/recover-stuck/`,
            body: {},
            onError: (resp) => {
                const detail = extractDetail(
                    resp.data,
                    `Failed to dispatch recover-stuck (HTTP ${resp.status})`,
                );
                if (resp.status === 409) {
                    dispatch(slice.actions.setActionError({
                        key: 'recoverStuckOutbox',
                        error: `Cannot recover: practice is archived. ${detail}`,
                    }));
                } else if (resp.status === 503) {
                    dispatch(slice.actions.setActionError({
                        key: 'recoverStuckOutbox',
                        error: `Recover dispatch failed — broker unreachable: ${detail}`,
                    }));
                } else {
                    dispatch(slice.actions.setActionError({
                        key: 'recoverStuckOutbox',
                        error: detail,
                    }));
                }
            },
            onSuccess: () => {
                dispatch(fetchDiagnostics(practiceId, { fresh: true }));
            },
        });
        return response;
    };
}

// --------------- PR #5 (FE) thunk: recommended-next-step ----------------

/**
 * fetchRecommendedNextStep — GET /practices/{id}/recommended-next-step/
 *
 * Returns the cockpit's CTA recommendation, computed BE-side so the FE
 * doesn't have to re-implement the priority order. Outbox health is
 * checked first — the operator never gets told to "Run Data Match" while
 * the forward queue is still draining (the original bug PR #5 fixes).
 *
 * Response shape:
 *   { key: string, body: string, outbox_summary: {...} }
 *
 * Errors are stored in panelErrors.recommendedNextStep so the cockpit can
 * fall back to the legacy inline pickStep() if the endpoint is unreachable.
 */
export function fetchRecommendedNextStep(practiceId) {
    return async () => {
        if (!practiceId) return null;
        try {
            dispatch(slice.actions.setPanelLoading({ key: 'recommendedNextStep', value: true }));
            dispatch(slice.actions.setPanelError({ key: 'recommendedNextStep', error: null }));
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/recommended-next-step/`,
            );
            if (response.status === 200 && response.data) {
                dispatch(slice.actions.setRecommendedNextStep(response.data));
            } else {
                const detail = extractDetail(
                    response.data,
                    `Failed to fetch recommendation (HTTP ${response.status})`,
                );
                dispatch(slice.actions.setPanelError({
                    key: 'recommendedNextStep',
                    error: detail,
                }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setPanelError({
                key: 'recommendedNextStep',
                error: message,
            }));
            return { ok: false, status: 0, error };
        } finally {
            dispatch(slice.actions.setPanelLoading({ key: 'recommendedNextStep', value: false }));
        }
    };
}

// --------------- Migration safety thunk ---------------------------------

/**
 * fetchMigrationSafety — GET /practices/{id}/migration-safety/
 *
 * Returns the aggregated "safe to advance / safe to roll back?" report.
 * The BE pulls together pre-flight, soak, parity drift, mirror health,
 * post-apply convergence, and V2-only activity into one decision
 * surface so the cockpit banner can render in one round-trip instead of
 * fanning five reads.
 *
 * Response shape (mirrors BE _build_migration_safety_report):
 *   {
 *     current_phase, current_canonical_side,
 *     tasks_v2_flag_enabled, tasks_v1_flag_enabled,
 *     mirror_paused, force_progressed, v4_plan_enabled,
 *     forward:  { verdict, target_phase, blockers, warnings },
 *     rollback: { verdict, target_phase, would_lose, blockers, warnings },
 *     summary_text, checked_at,
 *   }
 *
 * verdict ∈ {"safe", "warn", "blocked"}. Errors are stored in
 * panelErrors.migrationSafety so the banner can degrade gracefully
 * (render "safety status unavailable" rather than blank).
 */
export function fetchMigrationSafety(practiceId) {
    return async () => {
        if (!practiceId) return null;
        try {
            dispatch(slice.actions.setPanelLoading({ key: 'migrationSafety', value: true }));
            dispatch(slice.actions.setPanelError({ key: 'migrationSafety', error: null }));
            // 20s axios timeout: this endpoint runs `run_pre_flight_probes`
            // (5s budget + Celery `inspect()` broadcast cost) PLUS the
            // forward/rollback safety blocks (PracticeFeature lookups,
            // ParityCheckRun queries, MirrorOutbox counts, soak-time eval).
            // The default 10s window killed this call on staging with HTTP 0
            // even when the upstream pre-flight cache (shipped 2026-05-11)
            // is warm, because cold cache hits still pay the full probe
            // cost. 20s is comfortable headroom for the cold path; warm
            // requests resolve in <100ms regardless.
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/migration-safety/`,
                'GET',
                null,
                { timeout: 20000 },
            );
            if (response.status === 200 && response.data) {
                dispatch(slice.actions.setMigrationSafety(response.data));
            } else {
                const detail = extractDetail(
                    response.data,
                    `Failed to fetch migration-safety (HTTP ${response.status})`,
                );
                dispatch(slice.actions.setPanelError({
                    key: 'migrationSafety',
                    error: detail,
                }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setPanelError({
                key: 'migrationSafety',
                error: message,
            }));
            return { ok: false, status: 0, error };
        } finally {
            dispatch(slice.actions.setPanelLoading({ key: 'migrationSafety', value: false }));
        }
    };
}

// --------------- PR #6 (FE) thunks: drift drill-down + Force-mirror + Skip ---

/**
 * fetchParityDriftDetail — GET /parity-runs/{run_id}/drift-detail/?bucket=&id=
 *
 * Lazy per-row drift detail. Stashes the response in
 * `state.parityDriftDetail[`${bucket}:${id}`]` so re-opens of the same row
 * (and toggles between buckets) hit the cache instead of refetching.
 */
export function fetchParityDriftDetail({ runId, bucket, id } = {}) {
    return async () => {
        if (!runId || !bucket || id == null || id === '') return null;
        const cacheKey = `${bucket}:${id}`;
        try {
            dispatch(slice.actions.setPanelLoading({ key: 'parityDriftDetail', value: true }));
            dispatch(slice.actions.setPanelError({ key: 'parityDriftDetail', error: null }));
            const url = (
                `${BASE}/parity-runs/${runId}/drift-detail/`
                + `?bucket=${encodeURIComponent(bucket)}`
                + `&id=${encodeURIComponent(id)}`
            );
            const response = await callAdminApi(url);
            if (response.status === 200 && response.data) {
                dispatch(slice.actions.setParityDriftDetail({
                    key: cacheKey, data: response.data,
                }));
            } else {
                const detail = extractDetail(
                    response.data,
                    `Failed to load drift detail (HTTP ${response.status})`,
                );
                dispatch(slice.actions.setPanelError({
                    key: 'parityDriftDetail', error: detail,
                }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setPanelError({
                key: 'parityDriftDetail', error: message,
            }));
            return { ok: false, status: 0, error };
        } finally {
            dispatch(slice.actions.setPanelLoading({
                key: 'parityDriftDetail', value: false,
            }));
        }
    };
}

/**
 * forceMirror — POST /practices/{id}/force-mirror/
 *
 * Body: { direction: 'forward'|'back', reminder_id?: int, task_id?: str }.
 * One-click idempotent re-emission via the outbox; BE dispatches the worker
 * after enqueuing so the row is processed immediately.
 *
 * On success: refreshes the active drift-detail entry (so outbox state
 * updates), the parity-runs list, and the diagnostics snapshot. The success
 * snackbar is wired via `lastActionSuccess.forceMirror`.
 */
export function forceMirror({
    practiceId, direction, reminderId, taskId, runId, bucket, rowId,
} = {}) {
    return async () => {
        if (!practiceId || !direction) return null;
        const body = { direction };
        if (reminderId != null) body.reminder_id = reminderId;
        if (taskId != null) body.task_id = taskId;
        const response = await performAction({
            key: 'forceMirror',
            url: `${BASE}/practices/${practiceId}/force-mirror/`,
            body,
            onError: (resp) => {
                if (_maybeCapturePreFlightBlock(resp, 'forceMirror')) return;
                const detail = extractDetail(
                    resp.data,
                    `Failed to force-mirror (HTTP ${resp.status})`,
                );
                dispatch(slice.actions.setActionError({ key: 'forceMirror', error: detail }));
            },
            onSuccess: () => {
                if (runId && bucket && rowId != null) {
                    dispatch(fetchParityDriftDetail({ runId, bucket, id: rowId }));
                }
                if (practiceId) {
                    dispatch(fetchParityRuns(practiceId));
                    dispatch(fetchDiagnostics(practiceId, { fresh: true }));
                    // ExecuteResultsPanel's "Count check" reads
                    // practiceDetail.v1_reminder_count / v2_task_count to
                    // decide whether to show a "N V1 reminders not yet in V2"
                    // warning. After a Force-mirror lands a missing row, those
                    // counts move — refresh practiceDetail so the panel doesn't
                    // sit on the stale snapshot from execute time.
                    dispatch(fetchPracticeDetail(practiceId));
                    // Also refresh recommendation + soak eligibility because
                    // the queue snapshot they read will have changed.
                    dispatch(fetchRecommendedNextStep(practiceId));
                    dispatch(fetchMigrationSafety(practiceId));
                    dispatch(fetchPhaseAdvanceEligibility(practiceId));
                }
            },
        });
        return response;
    };
}

/**
 * skipDriftRow — POST /practices/{id}/skip-drift/
 *
 * Body: { map_id }. Tags the ledger row with `skipped_manual` so the parity
 * classifier excludes it from future drift counts. The confirm dialog lives
 * in the FE component (per PR #6 spec — Skip changes the parity result).
 *
 * On success: refreshes the active drift-detail entry (warnings list updates)
 * and re-runs the parity check so the cockpit reflects the new exclusion.
 */
export function skipDriftRow({
    practiceId, mapId, runId, bucket, rowId,
} = {}) {
    return async () => {
        if (!practiceId || mapId == null || mapId === '') return null;
        const response = await performAction({
            key: 'skipDriftRow',
            url: `${BASE}/practices/${practiceId}/skip-drift/`,
            body: { map_id: mapId },
            onError: (resp) => {
                const detail = extractDetail(
                    resp.data,
                    `Failed to skip row (HTTP ${resp.status})`,
                );
                dispatch(slice.actions.setActionError({ key: 'skipDriftRow', error: detail }));
            },
            onSuccess: () => {
                if (runId && bucket && rowId != null) {
                    dispatch(fetchParityDriftDetail({ runId, bucket, id: rowId }));
                }
                if (practiceId) {
                    // Re-run parity so the row drops off drift counts. The
                    // existing thunk seeds `activeParityRun` and starts the
                    // poll loop — no extra wiring needed here.
                    dispatch(runParityCheck(practiceId));
                }
            },
        });
        return response;
    };
}

// --------------- PR #13 thunks: V1/V2 feature-flag master control ---------

/**
 * Capture an HTTP 412 FEATURE_FLAG_PHASE_INVALID or FEATURE_FLAG_BOTH_DISABLED
 * response from `setFeatureFlags` into `lastFeatureFlagBlock` so the
 * FeatureFlagsPanel can render a structured inline alert (rather than
 * piggy-backing on `actionErrors.setFeatureFlags`, which is a single string).
 *
 * Returns true if the response was handled, false otherwise.
 */
function _maybeCaptureFeatureFlagBlock(resp) {
    if (!resp || resp.status !== 412) return false;
    const data = resp.data || {};
    if (
        data.code !== 'FEATURE_FLAG_PHASE_INVALID'
        && data.code !== 'FEATURE_FLAG_BOTH_DISABLED'
        && data.code !== 'FEATURE_FLAG_V1_NOT_TOGGLEABLE'
    ) {
        return false;
    }
    dispatch(slice.actions.setLastFeatureFlagBlock({
        code: data.code,
        current_phase: data.current_phase || null,
        requested_v1_enabled: !!data.requested_v1_enabled,
        requested_v2_enabled: !!data.requested_v2_enabled,
        reason: data.reason || data.detail || null,
        allowed_combinations: Array.isArray(data.allowed_combinations)
            ? data.allowed_combinations
            : null,
    }));
    return true;
}

/**
 * fetchFeatureFlagsState — GET /practices/{id}/feature-flags/
 *
 * Returns `{v1_enabled, v2_enabled, v1_flag_exists, current_phase,
 * allowed_combinations}`. Stashed on `state.featureFlags.data`. The cockpit
 * panel calls this on mount and after every successful flag change so the
 * legal-combinations chip strip stays in sync with the practice's phase.
 */
export function fetchFeatureFlagsState(practiceId) {
    return async () => {
        if (!practiceId) return null;
        try {
            dispatch(slice.actions.setPanelLoading({ key: 'featureFlags', value: true }));
            dispatch(slice.actions.setPanelError({ key: 'featureFlags', error: null }));
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/feature-flags/`,
            );
            if (response.status === 200 && response.data) {
                dispatch(slice.actions.setFeatureFlagsState(response.data));
            } else {
                const detail = extractDetail(
                    response.data,
                    `Failed to fetch feature flags (HTTP ${response.status})`,
                );
                dispatch(slice.actions.setPanelError({
                    key: 'featureFlags',
                    error: detail,
                }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setPanelError({
                key: 'featureFlags',
                error: message,
            }));
            return { ok: false, status: 0, error };
        } finally {
            dispatch(slice.actions.setPanelLoading({ key: 'featureFlags', value: false }));
        }
    };
}

/**
 * setFeatureFlags — POST /practices/{id}/feature-flags/
 *
 * Body: `{v1_enabled, v2_enabled, confirm: '<practice name>'}`. The BE
 * applies a phase-aware validation matrix; misaligned combinations come
 * back as 412 FEATURE_FLAG_PHASE_INVALID and the universal "at least one
 * flag enabled" invariant comes back as 412 FEATURE_FLAG_BOTH_DISABLED.
 * Both are surfaced via `lastFeatureFlagBlock` rather than the generic
 * action-error toast so the panel can render an inline alert.
 *
 * On success: refreshes feature-flags state, diagnostics, the recommended
 * next step (so a misaligned-flag rec disappears once corrected), and
 * the practice detail (so the cockpit's `v2_flag_enabled` chip stays in
 * sync — both surfaces write to the same PracticeFeature row).
 */
export function setFeatureFlags({
    practiceId, v1Enabled, v2Enabled, confirm,
} = {}) {
    return async () => {
        if (
            !practiceId
            || typeof v1Enabled !== 'boolean'
            || typeof v2Enabled !== 'boolean'
            || !confirm
        ) {
            return null;
        }
        // Clear any stale block before the new attempt so the alert
        // doesn't flash the previous reason while the request is in flight.
        dispatch(slice.actions.clearLastFeatureFlagBlock());
        const response = await performAction({
            key: 'setFeatureFlags',
            url: `${BASE}/practices/${practiceId}/feature-flags/`,
            body: { v1_enabled: v1Enabled, v2_enabled: v2Enabled, confirm },
            onError: (resp) => {
                if (_maybeCapturePreFlightBlock(resp, 'setFeatureFlags')) return;
                if (_maybeCaptureFeatureFlagBlock(resp)) {
                    // Phase-invalid / both-disabled / v1-not-toggleable —
                    // captured into lastFeatureFlagBlock; skip the generic
                    // toast so the panel's inline alert is the only surface.
                    return;
                }
                const detail = extractDetail(
                    resp.data,
                    `Failed to apply feature flags (HTTP ${resp.status})`,
                );
                if (resp.status === 400 && resp.data?.code === 'FEATURE_FLAG_CONFIRM_MISMATCH') {
                    dispatch(slice.actions.setActionError({
                        key: 'setFeatureFlags',
                        error: 'Confirmation must match the practice name exactly.',
                    }));
                } else if (resp.status === 503) {
                    dispatch(slice.actions.setStorageOutage({
                        code: 'CHECKPOINT_STORAGE_UNAVAILABLE',
                        message: detail,
                        action: 'setFeatureFlags',
                    }));
                    _clearGlobalErrorToast();
                    dispatch(slice.actions.setActionError({
                        key: 'setFeatureFlags',
                        error: `Checkpoint storage unreachable: ${detail}`,
                    }));
                } else {
                    dispatch(slice.actions.setActionError({
                        key: 'setFeatureFlags',
                        error: detail,
                    }));
                }
            },
            onSuccess: () => {
                dispatch(fetchFeatureFlagsState(practiceId));
                dispatch(fetchDiagnostics(practiceId, { fresh: true }));
                dispatch(fetchRecommendedNextStep(practiceId));
                dispatch(fetchMigrationSafety(practiceId));
                dispatch(fetchPracticeDetail(practiceId));
            },
        });
        return response;
    };
}

// --------------- PR — Beat heartbeat / Redis telemetry / Pre-flight bypass / Soak bypass

/**
 * fireHeartbeat — POST /practices/{id}/fire-heartbeat/
 *
 * Synchronously dispatches a scheduled-style parity check via Celery, exactly
 * the way the periodic beat job would. Unblocks the operator who would
 * otherwise wait up to 4 hours for the next scheduled heartbeat.
 *
 * 202 Accepted: { dispatched: true, async_task_id, triggered_by: 'scheduled' }.
 * After success we refetch pre-flight health so the operator sees the probe
 * recover (it usually flips back to green within a few seconds once the
 * worker logs the new ParityCheckRun started_at).
 */
export function fireHeartbeat(practiceId) {
    return async () => {
        if (!practiceId) return null;
        const response = await performAction({
            key: 'fireHeartbeat',
            url: `${BASE}/practices/${practiceId}/fire-heartbeat/`,
            body: {},
            onError: (resp) => {
                const detail = extractDetail(
                    resp.data,
                    `Failed to fire heartbeat (HTTP ${resp.status})`,
                );
                dispatch(slice.actions.setActionError({
                    key: 'fireHeartbeat',
                    error: detail,
                }));
            },
            onSuccess: () => {
                // The probe reads the latest ParityCheckRun started_at; give
                // the worker a beat to land its row, then refetch.
                setTimeout(() => {
                    dispatch(fetchPreFlightHealth(practiceId));
                }, 1500);
                dispatch(fetchPreFlightHealth(practiceId));
            },
        });
        return response;
    };
}

/**
 * fetchRedisTelemetry — GET /redis-telemetry/
 *
 * NOT practice-scoped — Redis is shared across the deployment, so the
 * Diagnose panel reads global telemetry (connected/maxclients, top sources,
 * Celery settings, DO cluster id when applicable). Errors are surfaced via
 * panelErrors.redisTelemetry; the panel renders a single error card and
 * still shows the static action-ladder guidance below.
 */
export function fetchRedisTelemetry({ fresh = false } = {}) {
    return async () => {
        try {
            dispatch(slice.actions.setPanelLoading({ key: 'redisTelemetry', value: true }));
            dispatch(slice.actions.setPanelError({ key: 'redisTelemetry', error: null }));
            const url = `${BASE}/redis-telemetry/${fresh ? '?fresh=1' : ''}`;
            const response = await callAdminApi(url);
            if (response.status === 200) {
                dispatch(slice.actions.setRedisTelemetry({
                    data: response.data,
                    lastFetchedAt: new Date().toISOString(),
                }));
            } else if (response.status === 0) {
                dispatch(slice.actions.setPanelError({
                    key: 'redisTelemetry',
                    error: 'Network timeout fetching Redis telemetry.',
                }));
            } else {
                const detail = extractDetail(
                    response.data,
                    `Failed to fetch Redis telemetry (HTTP ${response.status})`,
                );
                dispatch(slice.actions.setPanelError({
                    key: 'redisTelemetry',
                    error: detail,
                }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setPanelError({
                key: 'redisTelemetry',
                error: message,
            }));
            return { ok: false, status: 0, error };
        } finally {
            dispatch(slice.actions.setPanelLoading({ key: 'redisTelemetry', value: false }));
        }
    };
}

/**
 * fetchPreflightBypass — GET /practices/{id}/preflight-bypass/
 *
 * Snapshot of the current bypass state for this practice. Drives both the
 * BypassActiveBanner (when active) and the PreflightBypassModal (which
 * reads `allowed_in_environment` + `allowed_actions_catalog` to decide
 * whether to even allow opening, and which checkboxes to render).
 */
export function fetchPreflightBypass(practiceId) {
    return async () => {
        if (!practiceId) return null;
        try {
            dispatch(slice.actions.setPanelLoading({ key: 'preflightBypass', value: true }));
            dispatch(slice.actions.setPanelError({ key: 'preflightBypass', error: null }));
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/preflight-bypass/`,
            );
            if (response.status === 200) {
                dispatch(slice.actions.setPreflightBypass({
                    data: response.data,
                    lastFetchedAt: new Date().toISOString(),
                }));
            } else if (response.status === 0) {
                dispatch(slice.actions.setPanelError({
                    key: 'preflightBypass',
                    error: 'Network timeout fetching pre-flight bypass.',
                }));
            } else {
                const detail = extractDetail(
                    response.data,
                    `Failed to fetch pre-flight bypass (HTTP ${response.status})`,
                );
                dispatch(slice.actions.setPanelError({
                    key: 'preflightBypass',
                    error: detail,
                }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setPanelError({
                key: 'preflightBypass',
                error: message,
            }));
            return { ok: false, status: 0, error };
        } finally {
            dispatch(slice.actions.setPanelLoading({ key: 'preflightBypass', value: false }));
        }
    };
}

/**
 * createPreflightBypass — POST /practices/{id}/preflight-bypass/
 *
 * Body: { confirm: practiceName, duration_minutes (1..120), reason,
 *         actions: [str subset of allowed_actions_catalog] }.
 * Errors: 403 PREFLIGHT_BYPASS_NOT_ALLOWED, 400 PREFLIGHT_BYPASS_*.
 *
 * Production has this disabled — the BE returns 403 and the modal hides the
 * button when `allowed_in_environment === false`.
 */
export function createPreflightBypass(practiceId, {
    confirm, durationMinutes, reason, actions,
} = {}) {
    return async () => {
        if (!practiceId || !confirm || !reason) return null;
        const body = {
            confirm,
            duration_minutes: Number(durationMinutes) || 30,
            reason,
            actions: Array.isArray(actions) ? actions : [],
        };
        const response = await performAction({
            key: 'createPreflightBypass',
            url: `${BASE}/practices/${practiceId}/preflight-bypass/`,
            body,
            onError: (resp) => {
                const detail = extractDetail(
                    resp.data,
                    `Failed to activate pre-flight bypass (HTTP ${resp.status})`,
                );
                const code = resp.data?.code;
                let msg = detail;
                if (resp.status === 403 || code === 'PREFLIGHT_BYPASS_NOT_ALLOWED') {
                    msg = 'Pre-flight bypass is disabled in this environment.';
                } else if (code === 'PREFLIGHT_BYPASS_CONFIRM_MISMATCH') {
                    msg = 'Confirmation must match the practice name exactly.';
                } else if (code === 'PREFLIGHT_BYPASS_DURATION_INVALID') {
                    msg = 'Duration must be between 1 and 120 minutes.';
                } else if (code === 'PREFLIGHT_BYPASS_REASON_REQUIRED') {
                    msg = 'A reason is required.';
                } else if (code === 'PREFLIGHT_BYPASS_ACTIONS_INVALID') {
                    msg = 'Selected actions are not in the allow-list catalogue.';
                }
                dispatch(slice.actions.setActionError({
                    key: 'createPreflightBypass',
                    error: msg,
                }));
            },
            onSuccess: (resp) => {
                dispatch(slice.actions.setPreflightBypass({
                    data: resp.data,
                    lastFetchedAt: new Date().toISOString(),
                }));
                dispatch(fetchPreFlightHealth(practiceId));
            },
        });
        return response;
    };
}

/**
 * clearPreflightBypass — DELETE /practices/{id}/preflight-bypass/
 *
 * Body: { confirm: practiceName }. Same response shape as GET.
 *
 * `performAction` doesn't carry a body for DELETE, but `callAdminApi`
 * accepts a `data` argument for any method, so we use it directly here and
 * mirror the loading/error pattern.
 */
export function clearPreflightBypass(practiceId, { confirm } = {}) {
    return async () => {
        if (!practiceId || !confirm) return null;
        dispatch(slice.actions.setActionLoading({ key: 'clearPreflightBypass', value: true }));
        dispatch(slice.actions.setActionError({ key: 'clearPreflightBypass', error: null }));
        try {
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/preflight-bypass/`,
                'DELETE',
                { confirm },
            );
            const ok = response.status >= 200 && response.status < 300;
            if (ok) {
                dispatch(slice.actions.setLastActionSuccess('clearPreflightBypass'));
                dispatch(slice.actions.setPreflightBypass({
                    data: response.data,
                    lastFetchedAt: new Date().toISOString(),
                }));
                dispatch(fetchPreFlightHealth(practiceId));
            } else {
                const detail = extractDetail(
                    response.data,
                    `Failed to clear pre-flight bypass (HTTP ${response.status})`,
                );
                dispatch(slice.actions.setActionError({
                    key: 'clearPreflightBypass',
                    error: detail,
                }));
            }
            return response;
        } catch (error) {
            const message = error?.message || String(error);
            dispatch(slice.actions.setActionError({
                key: 'clearPreflightBypass',
                error: message,
            }));
            return { ok: false, status: 0, data: null, error };
        } finally {
            dispatch(slice.actions.setActionLoading({ key: 'clearPreflightBypass', value: false }));
        }
    };
}

/**
 * setPhaseWithSoakBypass — POST /practices/{id}/set-phase/?force_no_soak=true
 *
 * Body: { phase, confirm: practiceName, reason }. Same response shape as
 * `setPhase`. Errors: 400 SOAK_BYPASS_CONFIRM_MISMATCH | SOAK_BYPASS_REASON_REQUIRED
 * plus the usual pre-flight / storage outage paths inherited from the
 * standard set-phase handler.
 *
 * On success we clear the soak-time block banner (it's no longer relevant)
 * and refetch the same artefacts as setPhase.
 */
export function setPhaseWithSoakBypass(practiceId, phase, { confirm, reason } = {}) {
    return async () => {
        if (!practiceId || !phase || !confirm || !reason) return null;
        const body = { phase, confirm, reason };
        const response = await performAction({
            key: 'setPhaseWithSoakBypass',
            url: `${BASE}/practices/${practiceId}/set-phase/?force_no_soak=true`,
            body,
            onError: (resp) => {
                if (_maybeCapturePreFlightBlock(resp, 'setPhaseWithSoakBypass')) return;
                const detail = extractDetail(
                    resp.data,
                    `Failed to force-advance phase (HTTP ${resp.status})`,
                );
                const code = resp.data?.code;
                let msg = detail;
                if (code === 'SOAK_BYPASS_CONFIRM_MISMATCH') {
                    msg = 'Confirmation must match the practice name exactly.';
                } else if (code === 'SOAK_BYPASS_REASON_REQUIRED') {
                    msg = 'A reason is required to bypass the soak gate.';
                } else if (resp.status === 503) {
                    dispatch(slice.actions.setStorageOutage({
                        code: 'CHECKPOINT_STORAGE_UNAVAILABLE',
                        message: detail,
                        action: 'setPhaseWithSoakBypass',
                        targetPhase: phase,
                    }));
                    _clearGlobalErrorToast();
                    msg = `Checkpoint storage unreachable — phase change aborted. ${detail}`;
                }
                dispatch(slice.actions.setActionError({
                    key: 'setPhaseWithSoakBypass',
                    error: msg,
                }));
            },
            onSuccess: () => {
                dispatch(slice.actions.clearStorageOutage());
                dispatch(slice.actions.clearLastSoakTimeBlock());
                dispatch(fetchPracticeDetail(practiceId));
                dispatch(fetchCheckpoints(practiceId));
                dispatch(fetchPhaseAdvanceEligibility(practiceId));
                // Mirror of setPhase onSuccess — see note there. The soak
                // bypass is a phase change, so the FeatureFlagsPanel needs
                // the same refresh to avoid serving a stale `current_phase`
                // matrix and blocking otherwise-legal toggles.
                dispatch(fetchFeatureFlagsState(practiceId));
            },
        });
        return response;
    };
}


// ---------------------------------------------------------------------------
// Tasks V2 Migration v4 plan -- PR2.5. Template selection wizard thunks.
// ---------------------------------------------------------------------------


/**
 * GET /practices/{id}/migration-template-ambiguities/
 *
 * Returns the list of (group, treatment_item_id) combinations that have >1
 * candidate template + the lock state. The endpoint returns 404 when
 * TASKS_V2_MIGRATION_PREVIEW_V2 is OFF on the BE -- we treat that as
 * "feature not enabled, render nothing" via setTemplateSelectionUnavailable.
 *
 * Network errors / 5xx fall through to setError + a "loading=false"
 * cleanup; the panel shows a polite "couldn't check selections" stub.
 */
export function fetchMigrationTemplateAmbiguities(practiceId) {
    return async () => {
        if (!practiceId) return null;
        dispatch(slice.actions.setTemplateSelectionLoading(true));
        try {
            const response = await callAdminApi(
                // PR2.5b: include_unambiguous=true asks the BE for the
                // single-candidate (auto-resolved) buckets too, so the
                // operator can preview every (group, treatment) combo
                // their V1 reminders fall into — not only the truly
                // ambiguous ones. Rows come back tagged with
                // `is_ambiguous: false` and AmbiguityRow renders them
                // in a dimmed "system default" style.
                // PR2.5c: include_all_groups=true asks the BE to also
                // surface V1 groups that have NO reminders yet (so the
                // operator can pre-pick a default for future ones).
                // These rows are returned with `has_reminders: false`
                // and render with a "No reminders yet" chip.
                `${BASE}/practices/${practiceId}/migration-template-ambiguities/?include_unambiguous=true&include_all_groups=true`,
            );
            if (response.status === 404) {
                dispatch(slice.actions.setTemplateSelectionUnavailable());
                return null;
            }
            if (response.status !== 200) {
                dispatch(slice.actions.setError(
                    `Failed to load template ambiguities (HTTP ${response.status})`,
                ));
                return null;
            }
            dispatch(slice.actions.setTemplateSelectionSnapshot({
                ambiguities: response.data?.ambiguities ?? [],
                locked: !!response.data?.locked,
                lockedAt: response.data?.locked_at ?? null,
            }));
            return response.data;
        } catch (error) {
            dispatch(slice.actions.setError(error?.message || String(error)));
            return null;
        } finally {
            dispatch(slice.actions.setTemplateSelectionLoading(false));
        }
    };
}


/**
 * POST /practices/{id}/migration-template-overrides/
 *
 * Body: ``{overrides: [{v1_group, treatment_item_id, template_id}, ...]}``.
 *
 * On success: refetches ambiguities so the panel re-renders with the new
 * pre-selection. On 423 LOCKED: stores the structured error so the panel
 * can show a "locked" banner without overwriting the previous fetch's
 * ambiguities list.
 */
export function saveMigrationTemplateOverrides(practiceId, overrides) {
    return async () => {
        if (!practiceId) return null;
        dispatch(slice.actions.setTemplateOverridesSaving(true));
        dispatch(slice.actions.setTemplateOverridesError(null));
        try {
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/migration-template-overrides/`,
                'POST',
                { overrides },
            );
            if (response.status === 200) {
                dispatch(slice.actions.setTemplateOverridesSuccess({
                    ts: Date.now(),
                    count: (response.data?.overrides || []).length,
                }));
                // Refetch so the panel reflects any locked / candidate
                // changes the BE applied.
                dispatch(fetchMigrationTemplateAmbiguities(practiceId));
                return response.data;
            }
            if (response.status === 423) {
                dispatch(slice.actions.setTemplateOverridesError({
                    code: response.data?.code || 'TEMPLATE_SELECTION_LOCKED',
                    message: response.data?.detail
                        || 'Template selections are locked for this migration.',
                    lockedAt: response.data?.locked_at,
                }));
                // Refetch so the panel updates the locked banner.
                dispatch(fetchMigrationTemplateAmbiguities(practiceId));
                return null;
            }
            if (response.status === 404) {
                dispatch(slice.actions.setTemplateSelectionUnavailable());
                return null;
            }
            dispatch(slice.actions.setTemplateOverridesError({
                code: response.data?.code || 'OVERRIDES_SAVE_FAILED',
                message: response.data?.detail
                    || `Failed to save selections (HTTP ${response.status})`,
            }));
            return null;
        } catch (error) {
            dispatch(slice.actions.setTemplateOverridesError({
                code: 'NETWORK_ERROR',
                message: error?.message || String(error),
            }));
            return null;
        } finally {
            dispatch(slice.actions.setTemplateOverridesSaving(false));
        }
    };
}


// ---------------------------------------------------------------------------
// [PR1] Tasks V2 Migration — per-practice migration-mode toggle thunks.
//
// Two endpoints (auto-routed via the management ViewSet, no explicit URL):
//   GET  /practices/{id}/migration-mode/  → current mode + lock state
//   POST /practices/{id}/migration-mode/  → flip mode (practice-name gated)
//
// The BE returns 423 LOCKED once ReminderTaskMap rows exist OR
// template_selection_locked_at is set — we surface ``lock_reason`` so the
// dialog can render a precise <Alert severity="warning">.
// ---------------------------------------------------------------------------

function _unpackMigrationModePayload(data) {
    // Tolerate either { mode, set_at, set_by_user_email, locked, lock_reason }
    // or the same fields camel-cased — keeps the FE forgiving while the BE
    // is parallel-implemented.
    const d = data || {};
    return {
        value: d.mode ?? d.value ?? null,
        setAt: d.set_at ?? d.setAt ?? null,
        setByUserEmail: d.set_by_user_email ?? d.setByUserEmail ?? null,
        locked: !!(d.locked ?? false),
        lockReason: d.lock_reason ?? d.lockReason ?? null,
    };
}

/**
 * GET /practices/{id}/migration-mode/
 *
 * Loads the practice's current migration mode + lock state. Treats 404 as
 * "feature not yet deployed on BE" — leaves ``migrationMode.value === null``
 * so the toggle card renders nothing (parallel with the templateSelection
 * featureAvailable=false fallback at :339).
 */
export function fetchMigrationMode(practiceId) {
    return async () => {
        if (!practiceId) return null;
        dispatch(slice.actions.setMigrationModeLoading(true));
        try {
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/migration-mode/`,
            );
            if (response.status === 404) {
                // BE feature flag off / endpoint not deployed yet. Keep
                // value=null so the toggle card stays hidden — same posture
                // as the template-selection panel's 404 path.
                dispatch(slice.actions.setMigrationModeValue({
                    value: null,
                    setAt: null,
                    setByUserEmail: null,
                    locked: false,
                    lockReason: null,
                }));
                return null;
            }
            if (response.status !== 200) {
                dispatch(slice.actions.setError(
                    `Failed to load migration mode (HTTP ${response.status})`,
                ));
                return null;
            }
            dispatch(slice.actions.setMigrationModeValue(
                _unpackMigrationModePayload(response.data),
            ));
            return response.data;
        } catch (error) {
            dispatch(slice.actions.setError(error?.message || String(error)));
            return null;
        } finally {
            dispatch(slice.actions.setMigrationModeLoading(false));
        }
    };
}


/**
 * POST /practices/{id}/migration-mode/
 *
 * Body: ``{mode: 'checklist'|'one_to_one', confirm_practice_name: '...'}``.
 *
 * Response codes:
 *   200 → success; we refresh the slice from the response body.
 *   400 → bad mode or mismatched confirm; surfaced via lastSaveError.
 *   423 → LOCKED; lock_reason is captured both on the error and on the
 *         migrationMode.lockReason field so the dialog can disable Apply.
 *
 * The caller is the cockpit's MigrationModeConfirmDialog which decides
 * whether to close itself based on the returned ``ok`` flag — same shape
 * as the V2FlagToggleDialog pattern at :49-55 of that file.
 */
export function setMigrationMode(practiceId, mode, confirmPracticeName) {
    return async () => {
        if (!practiceId) return { ok: false, status: 0 };
        dispatch(slice.actions.setMigrationModeSaving(true));
        dispatch(slice.actions.setMigrationModeError(null));
        try {
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/migration-mode/`,
                'POST',
                { mode, confirm_practice_name: confirmPracticeName },
            );
            if (response.status === 200) {
                dispatch(slice.actions.setMigrationModeValue(
                    _unpackMigrationModePayload(response.data),
                ));
                dispatch(slice.actions.setMigrationModeSuccess({
                    ts: Date.now(),
                    mode,
                }));
                // Code-review M2 fix: re-fetch pre-flight health after a
                // successful mode flip so the panel reflects the BE's
                // freshly-invalidated Redis cache. Without this, the
                // server-side probe verdicts in state.preFlightHealth
                // stay stale until the operator clicks Refresh -- the
                // client-side filtering in PreFlightHealthPanel masks it
                // visually but downstream consumers of the slice see
                // stale data.
                dispatch(fetchPreFlightHealth(practiceId));
                return { ok: true, status: 200, data: response.data };
            }
            if (response.status === 423) {
                // Lock state — capture the reason on BOTH the error (for
                // any toast / inline alert) and on migrationMode.lockReason
                // (so the dialog's Apply button stays disabled across the
                // next open).
                const lockReason = response.data?.lock_reason
                    || response.data?.detail
                    || 'Migration mode is locked.';
                dispatch(slice.actions.setMigrationModeValue({
                    locked: true,
                    lockReason,
                }));
                dispatch(slice.actions.setMigrationModeError({
                    code: response.data?.code || 'MIGRATION_MODE_LOCKED',
                    message: response.data?.detail || lockReason,
                    lockReason,
                }));
                return { ok: false, status: 423, data: response.data };
            }
            if (response.status === 400) {
                dispatch(slice.actions.setMigrationModeError({
                    code: response.data?.code || 'MIGRATION_MODE_BAD_REQUEST',
                    message: response.data?.detail
                        || 'Invalid mode or practice-name confirmation.',
                }));
                return { ok: false, status: 400, data: response.data };
            }
            if (response.status === 404) {
                dispatch(slice.actions.setMigrationModeError({
                    code: 'MIGRATION_MODE_UNAVAILABLE',
                    message: 'Migration mode endpoint is not available.',
                }));
                return { ok: false, status: 404, data: response.data };
            }
            dispatch(slice.actions.setMigrationModeError({
                code: response.data?.code || 'MIGRATION_MODE_SAVE_FAILED',
                message: response.data?.detail
                    || `Failed to save migration mode (HTTP ${response.status})`,
            }));
            return { ok: false, status: response.status, data: response.data };
        } catch (error) {
            dispatch(slice.actions.setMigrationModeError({
                code: 'NETWORK_ERROR',
                message: error?.message || String(error),
            }));
            return { ok: false, status: 0 };
        } finally {
            dispatch(slice.actions.setMigrationModeSaving(false));
        }
    };
}


// ---------------------------------------------------------------------------
// [PR2] Tasks V2 Migration — V1 → V2 Category Map thunks.
//
// Two endpoints (auto-routed via the management ViewSet, no explicit URL):
//   GET  /practices/{id}/migration-category-rules/ → current rules + system
//        defaults + choice options + lock state
//   POST /practices/{id}/migration-category-rules/ → bulk-replace rules
//
// Same lock semantics as the template mapper:
//   - 423 LOCKED once template_selection_locked_at is set OR ReminderTaskMap
//     rows exist for the practice.
//   - 400 on bad values (unknown v1_group / v1_method / v2_category).
//   - 404 when the BE feature flag is OFF → setCategoryMapUnavailable so
//     the trigger card stays hidden.
// ---------------------------------------------------------------------------

function _unpackCategoryMapPayload(data) {
    // Tolerate every shape the BE has historically used:
    //   * spec form:   { options: { v1_groups, v1_methods, v2_categories } }
    //   * flat form:   { all_v1_groups, all_v1_methods, all_v2_categories }
    //     (what the BE actually serializes today — see
    //      apps/management/tasks_migration_views.py:migration_category_rules)
    //   * camel:       { options: { groups, methods, categories } }
    //
    // The flat ``all_*`` form was the immediate cause of every dropdown in
    // the V1 → V2 Category Map being empty -- the FE was only looking at
    // ``data.options.*``. Falling back to ``data.all_*`` keeps the UI working
    // without forcing a BE redeploy.
    const d = data || {};
    const opt = d.options || {};
    return {
        rules: Array.isArray(d.rules) ? d.rules : [],
        systemDefaults: d.system_defaults || d.systemDefaults || {},
        options: {
            groups:
                opt.v1_groups
                || opt.groups
                || d.all_v1_groups
                || [],
            methods:
                opt.v1_methods
                || opt.methods
                || d.all_v1_methods
                || [],
            categories:
                opt.v2_categories
                || opt.categories
                || d.all_v2_categories
                || [],
        },
        locked: !!(d.locked ?? false),
        lockReason: d.lock_reason ?? d.lockReason ?? null,
        lockedAt: d.locked_at ?? d.lockedAt ?? null,
    };
}

/**
 * GET /practices/{id}/migration-category-rules/
 *
 * Loads the practice's persisted category rules + system defaults + choice
 * options. Treats 404 as "feature not yet deployed on BE" — flips
 * ``categoryMap.featureAvailable`` so the trigger card renders nothing
 * (parallel with the templateSelection feature-OFF path).
 */
export function fetchCategoryRules(practiceId) {
    return async () => {
        if (!practiceId) return null;
        dispatch(slice.actions.setCategoryMapLoading(true));
        try {
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/migration-category-rules/`,
            );
            if (response.status === 404) {
                dispatch(slice.actions.setCategoryMapUnavailable());
                return null;
            }
            if (response.status !== 200) {
                dispatch(slice.actions.setError(
                    `Failed to load category rules (HTTP ${response.status})`,
                ));
                return null;
            }
            dispatch(slice.actions.setCategoryMapData(
                _unpackCategoryMapPayload(response.data),
            ));
            return response.data;
        } catch (error) {
            dispatch(slice.actions.setError(error?.message || String(error)));
            return null;
        } finally {
            dispatch(slice.actions.setCategoryMapLoading(false));
        }
    };
}


/**
 * POST /practices/{id}/migration-category-rules/
 *
 * Body: ``{rules: [{v1_group, v1_method, v2_category}, ...]}``. Bulk-replace
 * semantics — the BE wipes existing rules for the practice and writes the
 * payload list. ``v1_method`` may be empty string for a group-level rule.
 *
 * Response codes:
 *   200 → success; we refresh from the response body so the panel re-renders
 *         with persisted ids + timestamps.
 *   400 → bad values; surfaced via lastSaveError.
 *   423 → LOCKED; lock_reason captured both on the error and on
 *         ``categoryMap.lockReason`` so the dialog can render the alert.
 */
export function saveCategoryRules(practiceId, rules) {
    return async () => {
        if (!practiceId) return { ok: false, status: 0 };
        dispatch(slice.actions.setCategoryMapSaving(true));
        dispatch(slice.actions.setCategoryMapError(null));
        try {
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/migration-category-rules/`,
                'POST',
                { rules },
            );
            if (response.status === 200) {
                // Hydrate from the response body so the panel sees persisted
                // ids + created_at timestamps without a second round-trip.
                dispatch(slice.actions.setCategoryMapData(
                    _unpackCategoryMapPayload(response.data),
                ));
                dispatch(slice.actions.setCategoryMapSuccess({
                    ts: Date.now(),
                    count: (response.data?.rules || []).length,
                }));
                return { ok: true, status: 200, data: response.data };
            }
            if (response.status === 423) {
                const lockReason = response.data?.lock_reason
                    || response.data?.detail
                    || 'Category map is locked for this migration.';
                // Surface the lock reason on the error payload so the dialog
                // can render the alert. The canonical lock flag is refreshed
                // from the follow-up GET below (matches the template-mapper
                // posture in saveMigrationTemplateOverrides above).
                dispatch(slice.actions.setCategoryMapError({
                    code: response.data?.code || 'CATEGORY_MAP_LOCKED',
                    message: response.data?.detail || lockReason,
                    lockReason,
                }));
                // Refetch so the panel updates the locked banner from the
                // canonical GET payload (which also carries lockReason).
                dispatch(fetchCategoryRules(practiceId));
                return { ok: false, status: 423, data: response.data };
            }
            if (response.status === 400) {
                dispatch(slice.actions.setCategoryMapError({
                    code: response.data?.code || 'CATEGORY_MAP_BAD_REQUEST',
                    message: response.data?.detail
                        || 'One or more rules are invalid.',
                }));
                return { ok: false, status: 400, data: response.data };
            }
            if (response.status === 404) {
                dispatch(slice.actions.setCategoryMapUnavailable());
                return { ok: false, status: 404, data: response.data };
            }
            dispatch(slice.actions.setCategoryMapError({
                code: response.data?.code || 'CATEGORY_MAP_SAVE_FAILED',
                message: response.data?.detail
                    || `Failed to save category rules (HTTP ${response.status})`,
            }));
            return { ok: false, status: response.status, data: response.data };
        } catch (error) {
            dispatch(slice.actions.setCategoryMapError({
                code: 'NETWORK_ERROR',
                message: error?.message || String(error),
            }));
            return { ok: false, status: 0 };
        } finally {
            dispatch(slice.actions.setCategoryMapSaving(false));
        }
    };
}


// ── Canonical selectors ──────────────────────────────────────────────
//
// The cockpit historically had several panels each reading a different
// fallback chain to derive the same logical fact (drift count, soak
// status). Those chains drifted apart in subtle ways — different default
// values, different definitions of "running" — and surfaced as the
// "two screens disagree" data-consistency bug reported in the UX review.
//
// These selectors are the single source-of-truth that every consumer
// (top banner, HealthSummary, RecommendedNextStep, CriticalAlerts,
// ParityDriftPanel, SoakBanner, etc.) MUST read from. Adding more inline
// fallback chains in components is what created the bug — don't
// reintroduce them.
//

const _PARITY_TERMINAL_STATUSES = new Set([
    'succeeded', 'failed', 'completed', 'cancelled',
]);

/**
 * Canonical drift count for the practice currently loaded in the cockpit.
 *
 * Returns:
 *   - a finite number when the most recent terminal parity run has a
 *     usable drift_count, or when practiceDetail.drift_count is set;
 *   - null when neither source has a value yet (no parity run has
 *     completed for this practice and the practice detail bundle is
 *     either pre-load or doesn't carry a drift_count).
 *
 * Importantly, an in-flight parity run (drift_count=null while the
 * worker runs) is NOT used as a value — consumers should keep the
 * previous known number visible during the run, then transition once
 * the next terminal row lands. Use selectParityRunning() to render the
 * "running" affordance separately.
 *
 * Priority chain:
 *   1. Latest TERMINAL parity run with a non-null drift_count.
 *   2. practiceDetail.drift_count (refreshed via fetchPracticeDetail).
 */
export function selectDriftCount(state) {
    const parityRuns = state.tasksV2Migration?.parityRuns || [];
    const terminalWithCount = parityRuns.find((r) => r
        && _PARITY_TERMINAL_STATUSES.has(r.status)
        && r.drift_count != null);
    if (terminalWithCount) {
        const n = Number(terminalWithCount.drift_count);
        if (Number.isFinite(n)) return n;
    }
    const detail = state.tasksV2Migration?.practiceDetail?.drift_count;
    if (detail != null) {
        const n = Number(detail);
        if (Number.isFinite(n)) return n;
    }
    return null;
}

/**
 * True when a parity (data-match) check is currently in flight for the
 * practice loaded in the cockpit. Driven by activeParityRun's status —
 * the polling loop seeds this when Run Now is clicked and clears it
 * when the worker's terminal row lands.
 */
export function selectParityRunning(state) {
    const active = state.tasksV2Migration?.activeParityRun;
    if (!active || !active.status) return false;
    return !_PARITY_TERMINAL_STATUSES.has(active.status);
}

/**
 * Canonical phase-advance soak / eligibility payload.
 *
 * The BE returns the same logical payload via two endpoints:
 *   - GET /practices/{id}/phase-advance-eligibility/  (PRIMARY — used
 *     by set-phase's 412 SOAK_TIME_NOT_MET gate)
 *   - GET /practices/{id}/mirror-activity/  (SECONDARY — bundles a
 *     soak_status field as a convenience for the activity panel)
 *
 * Surfaces in the cockpit (Advance Phase menu, SoakBanner) MUST read
 * this canonical selector — not the secondary mirror-activity field —
 * so the cockpit cannot show contradictory soak progress (the bug
 * where Mirror Activity says "Soak complete" while Advance Phase says
 * "Needs 2 more clean Data Match runs").
 *
 * Returns the data object or null when the endpoint hasn't loaded yet.
 */
export function selectPhaseAdvanceEligibility(state) {
    return state.tasksV2Migration?.phaseAdvanceEligibility?.data || null;
}
